import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import API from '../api';
import usePermissions from '../hooks/usePermissions';
import FileDropZone from '../components/FileDropZone';
import { logDownload, logPrint } from '../utils/eventLog';
import { printedByLabel } from '../utils/userLabel';
import { getKstDateString } from '../utils/dateKst';

// ══════════════════════════════════════════════════════════════════════════
// 자산관리 — 2026-08-29 신규. 사용자가 첨부한 UbiPlus-AMS 분석 문서를 근거로
// 기준정보(품목/위치/거래처) → 자산관리(마스터+이력) → 현황조회/보고서 → 감가상각 →
// 재물조사(모바일 웹 QR스캔, PDA 제외) 5개 탭으로 구성했다. 다른 메뉴(인사관리 HRManagement.js,
// 계약관리 Contracts.js)와 동일한 화면 구성 규칙(색상/버튼/표/모달 스타일, 상단탭, 컬럼명 클릭정렬)을
// 그대로 따른다.
// 수리요청→완료, 대여→반납 2단계 흐름은 전자결재 연동 없이 "간단 상태버튼" 방식으로 구현했다
// (요청 시 사용자가 명시적으로 선택한 방식).
//
// 2026-08-29(2차) "우측상단버튼" 개편 — 인쇄/내보내기/관리항목/AI종합리포트/폐기항목/감가설정/
// 자산등록 7개 버튼을 화면 우측 상단에 항상 고정한다.
//  - 인쇄/내보내기는 업체관리(Companies.js) 등 다른 메뉴와 동일한 스타일/동작(새 창에 표를 그려
//    인쇄, xlsx 패키지로 클라이언트에서 바로 엑셀 생성)이며, 현재 활성화된 탭(및 하위탭)의 데이터를
//    대상으로 동작한다. 이를 위해 각 탭/하위탭 컴포넌트가 마운트될 때 registerPrintExport()로
//    자신의 onPrint/onExport 핸들러를 등록하고, 언마운트(=다른 탭으로 전환) 시 해제한다.
//  - 관리항목은 상단 5개 탭(기준정보/자산관리/현황조회·보고서/감가상각/재물조사)의 라벨/순서/노출
//    여부를 관리하고, 완전히 새로운 탭도 추가할 수 있다(DB: asset_menu_tabs). 기본 5개 탭은 실제
//    화면 기능이 연결되어 있어 삭제할 수 없고, 새로 추가한 탭은 간단한 게시판(asset_tab_notes)으로
//    렌더링된다.
//  - AI종합리포트는 경영분석(routes/ai.js)의 POST /ai/analyze/assets를 그대로 호출한다
//    (Tasks.js의 AiWeeklyReportModal과 동일한 UI 패턴).
//  - 폐기항목은 상태값이 '폐기'인 자산 목록을 팝업 모달로 보여주고 상태복원 기능을 제공한다.
//  - 감가설정은 감가상각 탭의 계정과목관리(계정 자체를 만드는 곳)와는 별개로, 이미 등록된
//    자산들에 감가상각계정/상각제외 여부를 개별/일괄로 빠르게 지정하는 도구다.
// ══════════════════════════════════════════════════════════════════════════

// 2026-08-31(3차) — 사용자 리포트: "관리항목에서 지운 탭이 새로고침/재시작하면 되살아나고
// 아이콘도 다시 생긴다". 원인은 (1) 백엔드 시딩 로직이 재시작마다 기본 탭을 재삽입하던 버그
// (database.js에서 최초 1회만 심도록 수정함)와 (2) 아래에 있던 "로드 전 폴백" 목록이 실제로는
// API가 이미 로드됐는데 목록이 비어 있는 경우(=사용자가 모든 기본 탭을 지운 경우)에도 계속
// 기본 8탭을 대신 그려주고 있었던 것. 사용자 요청("기본값으로 되어있는 사항은 무시하고
// 관리항목에 등록된 사항만 탭과 상단 카드로 표시")에 따라 폴백 자체를 없앴다 — 화면은 이제
// 오직 asset_menu_tabs(관리항목)에 실제로 들어있는 내용만 그린다. 아이콘(이모지)도 요청대로
// 제거했다.

// asset_type 코드 → 라벨(자산등록 폼·자산종합 카드 등에서 재사용). 사용자 요청으로 아이콘은 뺐다.
const ASSET_TYPE_META = {
  furniture: { label: '집기자산', icon: '🪑' },
  it:        { label: '전산자산', icon: '💻' },
  software:  { label: '소프트웨어', icon: '💿' },
  equipment: { label: '장비자산', icon: '🛠️' },
  tool:      { label: '공구자산', icon: '🔧' },
  supplies:  { label: '비품자산', icon: '📦' },
};

// 2026-08-31 — 요약 카드를 업무관리(Tasks.js)의 상단 통계카드와 같은 스타일(색상배경+테두리)로
// 맞추기 위한 자산유형별 색상표. Tasks.js의 STATUS_COLOR/STATUS_BG와 동일한 톤 조합을 우선
// 재사용하고, 자산유형 수(전체+6개=7개)에 맞춰 부족한 색상만 다른 화면(감가설정 #B45309,
// AI리포트 #7C3AED 등)에서 이미 쓰던 톤으로 보충했다.
const ASSET_SUMMARY_COLORS = {
  overview:     { color: '#185FA5', bg: '#E6F1FB' },
  furniture:    { color: '#854F0B', bg: '#FAEEDA' },
  it:           { color: '#0F6E56', bg: '#E1F5EE' },
  software:     { color: '#7C3AED', bg: '#F3E8FF' },
  equipment:    { color: '#993556', bg: '#FBEAF0' },
  tool:         { color: '#0F766E', bg: '#CCFBF1' },
  supplies:     { color: '#B45309', bg: '#FEF3C7' },
  depreciation: { color: '#475569', bg: '#F1F5F9' },
};
// 위 색상표에 없는 탭(관리항목에서 새로 추가한 커스텀 탭 등)에 쓰는 기본 색상.
const ASSET_SUMMARY_COLOR_FALLBACK = { color: '#6b7280', bg: '#F3F4F6' };

// 다른 메뉴와 동일한 디자인 규격(HRManagement.js C/th/td/input/btn과 동일)
const C = { primary: '#185FA5', border: '#e5e7eb', bg: '#f8fafc', text: '#333', sub: '#888' };
const btn = (bg, color = '#fff') => ({ background: bg, color, border: 'none', borderRadius: 8, padding: '10px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' });
const smallBtn = (bg, color = '#fff') => ({ background: bg, color, border: 'none', borderRadius: 6, padding: '6px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' });
const outlineBtn = { background: '#fff', color: '#555', border: '1px solid #ddd', borderRadius: 8, padding: '10px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' };
const input = { padding: '10px 14px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', outline: 'none' };
const th = { padding: '10px 10px', fontSize: 13, fontWeight: 600, color: '#666', background: '#f8fafc', textAlign: 'center', whiteSpace: 'nowrap', borderBottom: '1px solid #e5e7eb' };
const td = { padding: '9px 10px', fontSize: 13.5, color: C.text, borderTop: '1px solid #f0f0f0', textAlign: 'center' };
const labelStyle = { fontSize: 13, color: '#555', fontWeight: 500, display: 'block', marginBottom: 4 };
const formInput = { width: '100%', padding: '9px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', marginBottom: 12, outline: 'none' };

function Section({ title, action, children }) {
  return (
    <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: 18 }}>
      {(title || action) && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ fontSize: 15, fontWeight: 600 }}>{title}</div>
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

function useSort(list, defaultKey = null) {
  const [sortKey, setSortKey] = useState(defaultKey);
  const [sortDir, setSortDir] = useState('asc');
  const toggleSort = (key) => { if (!key) return; if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortKey(key); setSortDir('asc'); } };
  const sorted = useMemo(() => {
    if (!sortKey) return list;
    const copy = [...list];
    copy.sort((a, b) => {
      let va = a?.[sortKey], vb = b?.[sortKey];
      if (va == null) va = ''; if (vb == null) vb = '';
      const na = Number(va), nb = Number(vb);
      const cmp = (va !== '' && vb !== '' && !isNaN(na) && !isNaN(nb)) ? na - nb : String(va).localeCompare(String(vb), 'ko');
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [list, sortKey, sortDir]);
  return { sorted, sortKey, sortDir, toggleSort };
}
function Sortable({ label, sortKey, activeKey, dir, onClick }) {
  const active = activeKey === sortKey;
  return (
    <span onClick={() => onClick(sortKey)} style={{ cursor: 'pointer', userSelect: 'none', color: active ? '#185FA5' : undefined, fontWeight: active ? 700 : undefined }}>
      {label}{active ? (dir === 'asc' ? ' ▲' : ' ▼') : ''}
    </span>
  );
}

// 2026-08-31(3차) — 탭/카드 라벨 앞에 붙는 이모지 아이콘을 화면 표시 단계에서 한번 더 걷어낸다.
// (DB 쪽도 기본 라벨에서 이모지를 뺐지만, 예전 데이터가 아직 남아있는 경우에 대한 안전장치)
const stripIcon = (label) => String(label || '').replace(/^[\p{Extended_Pictographic}️‍]+\s*/u, '');
const moneyFmt = (n) => (n || n === 0) ? Number(n).toLocaleString('ko-KR') : '0';
const onlyDigits = (s) => String(s || '').replace(/[^\d]/g, '');
const formatComma = (s) => { const d = onlyDigits(s); return d ? Number(d).toLocaleString('ko-KR') : ''; };
// 2026-09-13 추가 — 자산 상세/인쇄에서 "정액법, 20%/년"처럼 적용된 감가상각 방법+율을 한 줄로
// 표기하기 위한 헬퍼. attachAutoResidual(백엔드)이 각 자산 row에 붙여주는 dep_method/
// dep_useful_life/dep_rate를 사용한다(정액법은 100÷내용연수, 정률법은 저장된 dep_rate 그대로).
function assetDepLabel(a) {
  if (!a || !a.dep_method) return null;
  const rate = a.dep_method === '정률법'
    ? (a.dep_rate != null ? Number(a.dep_rate) : null)
    : (a.dep_useful_life ? 100 / Number(a.dep_useful_life) : null);
  return `${a.dep_method}${rate != null ? `, ${rate.toFixed(1)}%/년` : ''}`;
}

const STATUS_COLOR = {
  '사용': { color: '#0F6E56', bg: '#E1F5EE' }, '수리': { color: '#854F0B', bg: '#FAEEDA' },
  '대여': { color: '#185FA5', bg: '#E6F1FB' }, '불용': { color: '#666', bg: '#eee' },
  '분실': { color: '#DC2626', bg: '#FEE2E2' }, '폐기': { color: '#fff', bg: '#444' }, '보관': { color: '#6d28d9', bg: '#EDE4FB' },
};
function statusStyleOf(name) {
  if (STATUS_COLOR[name]) return STATUS_COLOR[name];
  let hash = 0; for (let i = 0; i < (name || '').length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  const palette = Object.values(STATUS_COLOR);
  return palette[hash % palette.length];
}
function StatusBadge({ status }) {
  if (!status) return <span>-</span>;
  const s = statusStyleOf(status);
  return <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 20, fontWeight: 600, background: s.bg, color: s.color }}>{status}</span>;
}

// 인증받은 API를 blob으로 받아 새 iframe에서 인쇄(라벨 PDF 등) — Sales.js/HRManagement.js와 동일한 방식
async function printPdfFromApi(url) {
  try {
    const res = await API.get(url, { responseType: 'blob' });
    const blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
    iframe.src = blobUrl;
    document.body.appendChild(iframe);
    iframe.onload = () => { setTimeout(() => { iframe.contentWindow.print(); }, 500); };
  } catch (e) { alert('라벨 생성 중 오류가 발생했습니다.'); }
}
async function downloadBlob(url, filename) {
  try {
    const res = await API.get(url, { responseType: 'blob' });
    const blobUrl = URL.createObjectURL(res.data);
    const a = document.createElement('a'); a.href = blobUrl; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  } catch (e) { alert('다운로드 중 오류가 발생했습니다.'); }
}

function currentUser() { try { return JSON.parse(sessionStorage.getItem('user') || '{}'); } catch (e) { return {}; } }

// 공용 화면인쇄 — 업체관리(Companies.js) 등 다른 메뉴와 동일한 스타일/동작: 새 창에 표를 그려서
// 인쇄하고 하단에 출력일시/출력자를 표기한다("2. 인쇄, 내보내기 - 다른 메뉴에 있는 버튼의
// 스타일과 기능이 같음" 요청 반영).
function printListWindow({ menu, title, columns, rows, listName }) {
  const user = currentUser();
  const theadHtml = `<tr style="background:#f0f0f0;">${columns.map(c => `<th>${c.label}</th>`).join('')}</tr>`;
  const bodyHtml = rows.map(r => `<tr>${columns.map(c => `<td>${c.value(r) ?? '-'}</td>`).join('')}</tr>`).join('') ||
    `<tr><td colspan="${columns.length}" style="padding:20px;color:#999;">데이터가 없습니다.</td></tr>`;
  const html = `
    <h2 style="text-align:center;margin-bottom:12px;">${title}</h2>
    <table border="1" cellpadding="6" cellspacing="0" style="width:100%;border-collapse:collapse;font-size:12px;">
      <thead>${theadHtml}</thead><tbody>${bodyHtml}</tbody>
    </table>`;
  const now = new Date();
  const printDT = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  const footerHtml = `<div style="display:flex;justify-content:space-between;margin-top:14px;padding-top:8px;border-top:1px solid #ddd;font-size:11px;color:#555;">
      <span>출력일시: ${printDT}</span><span>출력자: ${printedByLabel(user)}</span>
    </div>`;
  const pw = window.open('', '_blank', 'width=1200,height=900');
  pw.document.write(`<!DOCTYPE html><html><head><title>${title}</title>
    <style>@page{size:A4 landscape;margin:0}body{font-family:sans-serif;margin:0;padding:10mm;box-sizing:border-box}
    table{border-collapse:collapse;width:100%}th,td{border:1px solid #ccc;padding:5px 8px;text-align:center}</style>
    </head><body>${html}${footerHtml}</body></html>`);
  pw.document.close(); pw.focus();
  logPrint(menu, listName);
  setTimeout(() => { pw.print(); pw.close(); }, 500);
}

// 2026-09 요청("인쇄버튼을 누르면 입력된 정보와 라이선스, 사진을 같이 출력") — 자산 상세정보(라벨/값
// 표)와 등록된 사진, 라이선스 첨부파일(이미지인 경우 그대로 삽입, 그 외 형식은 파일명만 안내)을
// 한 인쇄창에 함께 그려서 인쇄한다. /uploads 경로는 쿠키 기반 인증(requireUploadAuth)이라 같은
// 브라우저(window.open으로 연 새 창)에서는 별도 토큰 처리 없이 <img src="/uploads/..">가 그대로
// 동작한다(AssetAttachmentsPanel의 첨부파일 링크와 동일한 방식).
// 팝업 차단 회피를 위해 창은 호출부(버튼 클릭 핸들러)에서 동기적으로 미리 열어(pw) 넘겨받고,
// 여기서는 그 창에 내용을 채워 넣기만 한다. 이미지 로딩이 끝난 뒤 인쇄창이 뜨도록 인쇄용 HTML
// 안에 이미지 로드 완료 대기 스크립트를 포함시킨다(고정 setTimeout만으로는 이미지가 늦게
// 뜨는 환경에서 빈 사진으로 인쇄될 수 있음).
function renderAssetPrintWindow(pw, a, licenseFiles) {
  const user = currentUser();
  const IMG_EXT = /\.(jpg|jpeg|png|gif|webp|bmp)$/i;
  const fieldRows = [
    ['구분', a.division_label], ['관리번호', a.asset_code], ['모델명', a.model_name], ['상세규격', a.spec], ['수량', a.qty],
    ['라이선스', a.license], ['시리얼번호', a.serial_no], ['RFID', a.rfid_code],
    ['사용부서', a.department], ['담당자', a.manager_name], ['담당자 연락처', a.manager_contact],
    ['사용자', a.user_name], ['구매일', a.acquire_date],
    ['구매금액(VAT포함)', a.acquire_price ? `${moneyFmt(a.acquire_price)}원` : null],
    ['잔존가액', (a.auto_residual_value ?? a.residual_value)
      ? `${moneyFmt(a.auto_residual_value ?? a.residual_value)}원${a.residual_auto ? ' (자동계산)' : ''}${assetDepLabel(a) ? ` (감가상각 : ${assetDepLabel(a).replace(', ', ' · ')})` : ''}`
      : null],
    ['갱신/만료일', a.is_permanent ? '영구' : a.renew_expire_date], ['구매처', a.vendor_name],
    ['AS담당자', a.as_manager_name], ['AS담당자 연락처', a.as_manager_contact],
    ['상각제외', a.depreciation_exempt ? '예' : null],
    ['비고', a.note], ['등록자', a.created_by], ['등록일', a.created_at?.slice(0, 10)],
  ].filter(([, v]) => v);
  const rowsHtml = fieldRows.map(([label, val]) => `
    <tr><th style="width:120px;background:#f8fafc;text-align:left;padding:7px 10px;border:1px solid #ccc;font-size:12px;color:#666;">${label}</th>
      <td style="padding:7px 10px;border:1px solid #ccc;font-size:12px;">${val}</td></tr>`).join('');

  const photoHtml = a.image_path ? `
    <div style="margin-top:18px;">
      <div style="font-size:12px;color:#666;margin-bottom:6px;font-weight:600;">사진</div>
      <img src="/uploads/${a.image_path}" style="max-width:100%;max-height:320px;object-fit:contain;border:1px solid #ccc;border-radius:4px;" />
    </div>` : '';

  const licenseHtml = (licenseFiles && licenseFiles.length) ? `
    <div style="margin-top:18px;">
      <div style="font-size:12px;color:#666;margin-bottom:6px;font-weight:600;">라이선스 첨부</div>
      ${licenseFiles.map(f => IMG_EXT.test(f.file_name)
        ? `<img src="/uploads/${f.file_name}" style="max-width:100%;max-height:320px;object-fit:contain;border:1px solid #ccc;border-radius:4px;margin-bottom:8px;display:block;" />`
        : `<div style="padding:8px 10px;border:1px solid #ccc;border-radius:4px;font-size:12px;color:#555;margin-bottom:8px;">📎 ${f.orig_name || f.file_name} (이 형식은 인쇄 미리보기에 포함되지 않습니다 — 파일로 별도 확인해주세요)</div>`
      ).join('')}
    </div>` : '';

  const now = new Date();
  const printDT = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

  pw.document.open();
  pw.document.write(`<!DOCTYPE html><html><head><title>${a.asset_name} 인쇄</title>
    <style>@page{margin:14mm}body{font-family:sans-serif;margin:0;padding:0;color:#222;}
    table{border-collapse:collapse;width:100%;}h2{margin:0 0 14px;font-size:18px;}</style>
    </head><body>
    <h2>${a.asset_name}</h2>
    <table>${rowsHtml}</table>
    ${photoHtml}${licenseHtml}
    <div style="display:flex;justify-content:space-between;margin-top:20px;padding-top:8px;border-top:1px solid #ccc;font-size:11px;color:#555;">
      <span>출력일시: ${printDT}</span><span>출력자: ${printedByLabel(user)}</span>
    </div>
    <script>
      window.onload = function () {
        var imgs = document.images, total = imgs.length, loaded = 0;
        function done() { window.print(); }
        if (!total) { done(); return; }
        for (var i = 0; i < total; i++) {
          if (imgs[i].complete) { loaded++; }
          else { imgs[i].addEventListener('load', function () { if (++loaded === total) done(); }); imgs[i].addEventListener('error', function () { if (++loaded === total) done(); }); }
        }
        if (loaded === total) done();
      };
    </script>
    </body></html>`);
  pw.document.close();
  pw.focus();
  logPrint('assets', a.asset_name);
}

// 공용 엑셀 내보내기 — 업체관리(Companies.js)와 동일하게 xlsx 패키지로 클라이언트에서 바로 생성
function exportListXlsx({ menu, filename, sheetName, columns, rows }) {
  const data = rows.map(r => Object.fromEntries(columns.map(c => [c.label, c.value(r) ?? ''])));
  const ws = XLSX.utils.json_to_sheet(data);
  ws['!cols'] = columns.map(c => ({ wch: c.wch || 14 }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, (sheetName || '목록').slice(0, 30));
  XLSX.writeFile(wb, filename);
  logDownload(menu, filename);
}

function fmtUser(u) { if (!u) return ''; return `${u.department || ''} ${u.position || ''} ${u.name}`.replace(/\s+/g, ' ').trim(); }

// ── 메인 컴포넌트 ─────────────────────────────────────────────────
export default function Assets() {
  const perm = usePermissions();
  const { hasDetail, canWrite } = perm;
  const canWriteAssets = canWrite('assets');
  const canManageCategory = hasDetail('assets', 'category_manage');
  const canDelete = hasDetail('assets', 'delete_allowed');
  const canDownload = hasDetail('assets', 'download_allowed');
  const canDepRead = hasDetail('assets', 'depreciation_read');
  const canDepWrite = hasDetail('assets', 'depreciation_write');
  const canAuditRead = hasDetail('assets', 'audit_read');
  const canAuditWrite = hasDetail('assets', 'audit_write');

  const [activeTab, setActiveTab] = useState('overview');
  const [menuTabs, setMenuTabs] = useState([]);
  const [menuTabsError, setMenuTabsError] = useState('');
  const loadMenuTabs = () => API.get('/assets/menu-tabs').then(r => { setMenuTabs(r.data); setMenuTabsError(''); })
    .catch(e => { console.error('관리항목(탭) 목록 조회 실패:', e); setMenuTabsError(e?.response?.data?.message || '관리항목 목록을 불러오지 못했습니다. 서버 상태를 확인해주세요.'); });

  // 우측상단 인쇄/내보내기 버튼이 "현재 활성 탭(및 하위탭)"을 대상으로 동작하도록, 각 탭/하위탭이
  // 마운트될 때 자신의 핸들러를 등록하고 언마운트 시 해제한다.
  const [printExport, setPrintExport] = useState(null);
  const registerPrintExport = useCallback((handlers) => setPrintExport(handlers), []);

  const [assetFormTrigger, setAssetFormTrigger] = useState(0);
  // 2026-08-31(4차) — 버그: 자산유형 탭(집기/전산/.../비품)은 서로 다른 컴포넌트 함수(FurnitureTab,
  // ITTab, ...)로 매핑돼 있어 탭을 전환할 때마다 AssetListTab이 완전히 새로 마운트된다. 예전 코드는
  // "assetFormTrigger가 0이 아니면 등록폼을 연다"를 마운트 시에도 그대로 실행해서, 세션 중 "자산등록"
  // 버튼을 한 번이라도 누른 뒤로는 아무 탭이나 클릭하기만 해도 등록폼이 떠 있었다(사용자가 "탭을
  // 누르면 등록화면이 나온다"고 보고한 버그). AssetsPage는 탭을 전환해도 리마운트되지 않으므로, 여기
  // 부모 레벨에 "이 trigger 값은 이미 폼을 열었다"를 기록하는 ref를 두고, 자식(AssetListTab)이 마운트/
  // 갱신될 때마다 이 ref로 소비 여부를 확인하게 한다. 그러면 (1) 같은 탭에서 등록 버튼을 누른 경우와
  // (2) 등록 버튼이 다른 탭으로 강제 전환시킨 경우에는 폼이 열리고, (3) 사용자가 그냥 다른 탭을
  // 클릭한 경우(트리거 값이 이전과 동일)에는 폼이 열리지 않는다.
  const formTriggerConsumedRef = useRef(0);
  const consumeAssetFormTrigger = useCallback((trigger) => {
    if (trigger && trigger !== formTriggerConsumedRef.current) {
      formTriggerConsumedRef.current = trigger;
      return true;
    }
    return false;
  }, []);

  // 2026-09(15차) 요청 — 고장수리 탭도 다른 탭들처럼 "등록" 버튼을 상단 페이지 헤더(인쇄/
  // 내보내기/관리항목 등과 같은 줄)에 두기로 하면서, 자산등록과 동일한 trigger-consume 패턴을
  // 고장수리 전용으로 하나 더 둔다.
  const [repairFormTrigger, setRepairFormTrigger] = useState(0);
  const repairFormTriggerConsumedRef = useRef(0);
  const consumeRepairFormTrigger = useCallback((trigger) => {
    if (trigger && trigger !== repairFormTriggerConsumedRef.current) {
      repairFormTriggerConsumedRef.current = trigger;
      return true;
    }
    return false;
  }, []);
  const openGlobalRepairForm = () => setRepairFormTrigger(c => c + 1);
  // 고장수리 탭 전용 검색/필터도 다른 유형탭과 동일하게 탭 줄 우측에서 관리한다.
  const [repairTypeFilter, setRepairTypeFilter] = useState('');
  const [repairStatusFilter, setRepairStatusFilter] = useState('');
  const [repairSearch, setRepairSearch] = useState('');
  const [showTabManage, setShowTabManage] = useState(false);
  const [showDepSet, setShowDepSet] = useState(false);

  // 2026-08-31(3차) — 상단 기준정보/재물조사/폐기항목 버튼을 삭제하면서, 그 버튼으로만 접근하던
  // 위치분류/거래처 관리 화면도 함께 폐지했다. vendors는 수리/대여 패널의 업체 선택용으로 계속 쓰이므로
  // 조회는 유지하되, itemCategories/locCategories(품목/위치 분류)는 더 이상 어디서도 쓰지 않아 제거한다.
  const [vendors, setVendors] = useState([]);
  const [userList, setUserList] = useState([]);
  const [settings, setSettings] = useState(null);

  // 2026-08-31 — 자산 현황 요약(전체/유형별 건수·금액)을 상단 고정 카드로 옮기면서, 특정 탭
  // (자산종합)에서만 로드하던 것을 AssetsPage 레벨로 올려 모든 탭에서 항상 보이게 한다.
  const [summary, setSummary] = useState(null);
  const loadSummary = () => API.get('/assets/summary').then(r => setSummary(r.data)).catch(() => {});

  const loadVendors = () => API.get('/assets/vendors').then(r => setVendors(r.data)).catch(() => {});
  const loadSettings = () => API.get('/assets/settings/current').then(r => setSettings(r.data)).catch(() => {});
  useEffect(() => {
    loadVendors(); loadSettings(); loadMenuTabs(); loadSummary();
    API.get('/assets/users/list').then(r => setUserList(r.data)).catch(() => {});
  }, []);

  // 관리항목에서 현재 탭을 삭제한 경우, 첫 번째 탭으로 자동 전환한다.
  useEffect(() => {
    if (!menuTabs.length) return;
    if (!menuTabs.find(t => t.tab_key === activeTab)) setActiveTab(menuTabs[0].tab_key);
  }, [menuTabs]); // eslint-disable-line

  // 2026-08-31(6차) — "자산등록" 버튼은 이제 자산유형 탭(집기/전산/.../비품)에 있을 때만
  // 보이므로(자산종합 등 비유형 탭에서는 아예 숨김), 더 이상 강제로 다른 탭으로 전환할 필요가
  // 없다. 버튼 자체가 유형탭에서만 렌더링되어 클릭 시 항상 현재 탭에서 폼이 열린다.
  const openGlobalAssetForm = () => {
    setAssetFormTrigger(c => c + 1);
  };

  // 2026-08-31(6차) — 목록 화면 전면개편: 위치/부서/상태 개별 필터를 없애고 검색창 하나만
  // 탭 우측 끝에 두기로 해서, 검색어를 AssetsPage 레벨로 끌어올려 탭 전환에 상관없이 동일한
  // 방식으로 각 유형탭(AssetListTab)에 내려준다.
  const [assetSearch, setAssetSearch] = useState('');
  // 2026-09(9차) 요청: "제외항목 포함" 토글을 없애고, 검색창 옆에 모두보기/장부등록/장부미등록
  // 필터 버튼을 추가해 장부등록 여부(list_exclude)로 목록을 골라볼 수 있게 한다.
  const [ledgerFilter, setLedgerFilter] = useState('all');

  const ctx = {
    perm, canWriteAssets, canManageCategory, canDelete, canDownload, canDepRead, canDepWrite, canAuditRead, canAuditWrite,
    vendors, userList, settings,
    loadVendors, loadSettings,
    registerPrintExport, setActiveTab,
    summary, loadSummary,
    consumeAssetFormTrigger,
    assetSearch, ledgerFilter,
  };

  // 2026-08-31(3차) — 더 이상 기본값 폴백을 쓰지 않는다. 관리항목(menuTabs)에 실제로 들어있는
  // 내용만 탭/카드로 그린다(사용자가 지운 탭은 다시 나타나지 않는다).
  const tabsForRender = menuTabs;
  const activeTabMeta = menuTabs.find(t => t.tab_key === activeTab);
  const ActiveBuiltin = activeTabMeta ? BUILTIN_TAB_COMPONENTS[activeTabMeta.tab_key] : null;
  // 2026-09(11차) 요청 — 관리항목으로 등록된 상단탭은 (자산종합/감가상각처럼 원래부터 목록형이
  // 아니었던 두 탭을 빼고는) 항상 비품/소프트웨어와 동일한 자산목록+등록폼 화면을 쓴다. 커스텀
  // 탭은 전용 wrapper 컴포넌트가 없어 ActiveBuiltin이 항상 null이므로, "activeTabMeta는 있는데
  // ActiveBuiltin이 없는 경우"만 걸러내면 곧 모든 커스텀 탭이 된다(list_mode 조건은 더 이상 쓰지 않음).
  // 2026-09(12차) 요청 — "고장수리형" 탭(tab_type='repair')은 RepairLogTab이 자체적으로
  // 검색/필터/등록 UI를 갖추고 있으므로, 상단 공통 검색창·자산등록 버튼(자산목록형 전용)은
  // 이 탭에서는 숨긴다.
  const isRepairTab = activeTabMeta && activeTabMeta.tab_type === 'repair';
  const isListTab = !!(ASSET_TYPE_META[activeTab] || (activeTabMeta && !ActiveBuiltin && !isRepairTab));
  // 자산등록 버튼/등록폼 제목에 쓸 탭 이름(아이콘 제거). 자산유형 6종은 ASSET_TYPE_META 라벨을,
  // 관리항목 커스텀 탭은 탭 라벨 그대로 사용한다.
  const activeTypeLabel = ASSET_TYPE_META[activeTab]?.label || (activeTabMeta ? stripIcon(activeTabMeta.label) : '');

  return (
    <div style={{ padding: 24, maxWidth: 1690 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>🗃️ 자산관리</h2>
          <p style={{ fontSize: 13, color: '#888', margin: '4px 0 0' }}>회사 고정자산을 등록·조회하고 이력, 감가상각, 재물조사를 관리합니다.</p>
        </div>
        <div className="hdr-actions" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <button onClick={() => printExport?.onPrint?.()} disabled={!printExport?.onPrint}
            style={{ ...btn('#475569'), opacity: printExport?.onPrint ? 1 : 0.45, cursor: printExport?.onPrint ? 'pointer' : 'not-allowed' }}>🖨️ 인쇄</button>
          <button onClick={() => printExport?.onExport?.()} disabled={!printExport?.onExport}
            style={{ ...btn('#16a34a'), opacity: printExport?.onExport ? 1 : 0.45, cursor: printExport?.onExport ? 'pointer' : 'not-allowed' }}>📥 내보내기</button>
          <button onClick={() => setShowTabManage(true)} style={btn('#475569')}>📑 관리항목</button>
          {canDepWrite && <button onClick={() => setShowDepSet(true)} style={btn('#B45309')}>📉 감가설정</button>}
          {/* 2026-08-31(6차) — 자산종합처럼 자산유형이 아닌 탭에서는 자산등록 버튼을 숨긴다
              (해당 요청: "자산종합에서는 자산등록 버튼을 숨김"). */}
          {canWriteAssets && isListTab && <button onClick={openGlobalAssetForm} style={btn('#185FA5')}>✍️ {activeTypeLabel ? `${activeTypeLabel}등록` : '자산등록'}</button>}
          {/* 2026-09(15차) 요청 — 고장수리 탭도 등록 버튼을 다른 탭과 같은 자리(우측 상단
              공통 버튼 줄)에 둬서 화면 구성에 통일감을 준다. */}
          {canWriteAssets && isRepairTab && <button onClick={openGlobalRepairForm} style={btn('#185FA5')}>🔧 {activeTypeLabel ? `${activeTypeLabel}등록` : '고장수리등록'}</button>}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 18, borderBottom: `1px solid ${C.border}`, flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {tabsForRender.length === 0 && (
            <div style={{ padding: '10px 4px', fontSize: 13, color: '#aaa' }}>
              {menuTabsError || '관리항목에 등록된 탭이 없습니다. 우측 상단 "관리항목" 버튼에서 탭을 추가해주세요.'}
            </div>
          )}
          {tabsForRender.map(t => (
            <button key={t.tab_key} onClick={() => setActiveTab(t.tab_key)}
              style={{ padding: '10px 18px', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600,
                borderBottom: activeTab === t.tab_key ? '2px solid #185FA5' : '2px solid transparent',
                background: 'none', color: activeTab === t.tab_key ? '#185FA5' : '#888' }}>
              {stripIcon(t.label)}
            </button>
          ))}
        </div>
        {/* 2026-08-31(6차) — 유형별 목록 화면(위치/부서/상태 필터)을 없애고 검색창 하나만
            탭 줄 우측 끝으로 옮긴다. 자산유형 탭에 있을 때만 의미가 있으므로 그때만 보여준다. */}
        {isListTab && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            {/* 2026-09(9차) 요청: 장부등록 여부 필터 버튼 3종을 검색창 옆에 배치 */}
            <div style={{ display: 'flex', gap: 4 }}>
              {[['all', '모두보기'], ['registered', '장부등록'], ['excluded', '장부미등록']].map(([key, label]) => (
                <button key={key} onClick={() => setLedgerFilter(key)}
                  style={{ padding: '6px 12px', borderRadius: 6, fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                    border: ledgerFilter === key ? '1px solid #185FA5' : '1px solid #d1d5db',
                    background: ledgerFilter === key ? '#185FA5' : '#fff',
                    color: ledgerFilter === key ? '#fff' : '#555' }}>{label}</button>
              ))}
            </div>
            <input value={assetSearch} onChange={e => setAssetSearch(e.target.value)}
              placeholder="🔍 자산명/모델명/시리얼/관리번호 검색" style={{ ...input, width: 260 }} />
          </div>
        )}
        {/* 2026-09(15차) 요청 — 고장수리 탭의 유형/상태/검색 필터를 다른 유형탭의 검색창과
            같은 자리(탭 줄 우측 끝)로 옮겨 화면 구성 통일감을 준다. */}
        {isRepairTab && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
            <select value={repairTypeFilter} onChange={e => setRepairTypeFilter(e.target.value)} style={{ ...input, width: 130 }}>
              <option value="">전체 유형</option>
              {REPAIR_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select value={repairStatusFilter} onChange={e => setRepairStatusFilter(e.target.value)} style={{ ...input, width: 110 }}>
              <option value="">전체 상태</option>
              {REPAIR_STATUS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <input value={repairSearch} onChange={e => setRepairSearch(e.target.value)}
              placeholder="🔍 시설명/위치/대처방안/업체 검색" style={{ ...input, width: 260 }} />
          </div>
        )}
      </div>

      {/* 2026-09(20차) 요청 — "전체 자산 수량/전체 자산금액/전체 잔존가액" 3개 요약 카드는
          자산종합(overview) 탭에서만 보이도록 되돌리고, 표시 위치도 탭 바로 아래로 이동한다
          (기존 19차 변경에서는 모든 탭에 항상 노출 + 탭 바 위쪽이었음). */}
      {activeTab === 'overview' && summary && (() => {
        const fmtMoneyTop = (n) => `${Math.round(n || 0).toLocaleString()}원`;
        const totalCount = summary.total?.count || 0;
        const totalUnregistered = summary.total?.unregistered_count || 0;
        const totalRegistered = Math.max(0, totalCount - totalUnregistered);
        const ribbon = [
          {
            label: '전체 자산 수량',
            value: `${totalCount}건`,
            inline: `(등록 ${totalRegistered}건, 미등록 ${totalUnregistered}건)`,
            color: '#185FA5', bg: '#E6F1FB',
          },
          { label: '전체 자산금액', value: fmtMoneyTop(summary.total?.total_price), color: '#0F766E', bg: '#CCFBF1' },
          { label: '전체 잔존가액', value: fmtMoneyTop(summary.total?.total_residual), color: '#7C3AED', bg: '#F3E8FF' },
        ];
        return (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
            {ribbon.map((s, i) => (
              <div key={i} style={{ background: s.bg, borderRadius: 12, padding: '14px 18px' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: s.color, marginBottom: 6 }}>{s.label}</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <span style={{ fontSize: 20, fontWeight: 700, color: s.color }}>{s.value}</span>
                  {s.inline && <span style={{ fontSize: 12.5, fontWeight: 600, color: s.color, opacity: 0.75 }}>{s.inline}</span>}
                </div>
              </div>
            ))}
          </div>
        );
      })()}

      {ActiveBuiltin && <ActiveBuiltin key={activeTabMeta.tab_key} {...ctx} assetFormTrigger={assetFormTrigger} />}
      {/* 2026-09(11차) 요청 — 관리항목에서 추가한 커스텀 탭은 전용 wrapper 컴포넌트가 없어도
          비품/소프트웨어와 동일한 <AssetListTab>(목록+등록폼+필터)을 항상 그대로 재사용해 렌더한다.
          더 이상 게시판형(TabNotesPanel)으로 빠지는 경로는 없다. */}
      {/* 2026-09(12차) 요청 — 관리항목에서 "고장수리형"으로 만든 탭은 자산목록+등록폼 대신
          전용 RepairLogTab(이력 목록+등록폼)을 렌더한다. 자산 등록 여부와 무관하게 화장실/
          수도설비/전기/자동문/유리문 등 기타 시설 고장수리 내역을 별도로 남길 수 있다. */}
      {!ActiveBuiltin && activeTabMeta && isRepairTab && (
        <RepairLogTab key={activeTabMeta.tab_key} tabId={activeTabMeta.id} label={stripIcon(activeTabMeta.label)}
          canWrite={canWriteAssets} canDelete={canDelete} registerPrintExport={registerPrintExport}
          repairFormTrigger={repairFormTrigger} consumeRepairFormTrigger={consumeRepairFormTrigger}
          typeFilter={repairTypeFilter} statusFilter={repairStatusFilter} search={repairSearch} />
      )}
      {!ActiveBuiltin && activeTabMeta && !isRepairTab && (
        <AssetListTab key={activeTabMeta.tab_key} {...ctx} assetFormTrigger={assetFormTrigger}
          assetType={activeTabMeta.tab_key} typeLabel={stripIcon(activeTabMeta.label)} />
      )}

      {showTabManage && <TabManageModal tabs={menuTabs} error={menuTabsError} canManage={canWriteAssets} onClose={() => setShowTabManage(false)} onChanged={loadMenuTabs} />}
      {showDepSet && <DepreciationQuickSetModal onClose={() => setShowDepSet(false)} />}
    </div>
  );
}

/* 2026-08-31(3차) — 상단 기준정보/재물조사/폐기항목 버튼 삭제에 따라 다음 컴포넌트를 제거했다(모두
   그 버튼들을 통해서만 접근 가능했던 dead code): MasterDataTab, CategoryTree, VendorManager, VendorForm,
   buildTree, AuditTab, AuditPlanForm, DisposedAssetsModal. 자산의 위치/구매처는 이제 직접입력(텍스트)이며,
   수리/대여 패널의 업체 선택(vendors)은 기존 데이터로 계속 동작한다. */

const overlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalStyle = { background: '#fff', borderRadius: 16, padding: 28, maxWidth: '90vw', maxHeight: '86vh', overflowY: 'auto' };
function ModalHeader({ title, onClose, extra }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
      <h3 style={{ fontSize: 17, fontWeight: 600, margin: 0 }}>{title}</h3>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        {extra}
        <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#999' }}>✕</button>
      </div>
    </div>
  );
}

// 2026-09 요청: 상세화면 헤더의 사진/라이선스 버튼 클릭시 뜨는 미리보기 모달에서, 파일 확장자에
// 따라 이미지는 그대로/PDF는 iframe/그 외는 다운로드 링크로 보여준다(HRManagement.js의
// FilePreviewModal과 동일한 판단 방식이나, 이쪽은 blob을 새로 받지 않고 쿠키인증되는
// /uploads 경로를 바로 쓴다 — 첨부파일 링크가 이미 같은 방식으로 동작 중이라 더 간단하다).
const PREVIEWABLE_IMG_RE = /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i;
const PREVIEWABLE_PDF_RE = /\.pdf$/i;
function FileThumb({ url, name }) {
  if (PREVIEWABLE_IMG_RE.test(name || url)) {
    return <img src={url} alt={name} style={{ maxWidth: '100%', maxHeight: 420, objectFit: 'contain', display: 'block', margin: '0 auto' }} />;
  }
  if (PREVIEWABLE_PDF_RE.test(name || url)) {
    return <iframe src={url} title={name} style={{ width: '100%', height: 420, border: 'none' }} />;
  }
  return (
    <div style={{ textAlign: 'center', padding: 30 }}>
      <div style={{ fontSize: 13, color: '#888', marginBottom: 12 }}>이 형식은 미리보기를 지원하지 않습니다.</div>
      <a href={url} target="_blank" rel="noreferrer" style={{ display: 'inline-block', padding: '8px 18px', background: C.primary, color: '#fff', borderRadius: 8, fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>⬇️ 다운로드</a>
    </div>
  );
}
// url이 있으면 단일파일(사진) 미리보기, files 배열이 있으면 다중첨부(라이선스) 미리보기.
function AssetFilePreviewModal({ title, url, files, onClose }) {
  return (
    <div style={overlayStyle}>
      <div style={{ ...modalStyle, width: 640 }} onClick={e => e.stopPropagation()}>
        <ModalHeader title={title} onClose={onClose} />
        <div style={{ background: '#f8fafc', borderRadius: 8, padding: 14 }}>
          {url && <FileThumb url={url} name={url} />}
          {files && files.length === 0 && <div style={{ textAlign: 'center', color: '#aaa', fontSize: 13, padding: 30 }}>첨부된 파일이 없습니다.</div>}
          {files && files.map((f, i) => (
            <div key={f.id} style={{ marginBottom: i < files.length - 1 ? 16 : 0 }}>
              <div style={{ fontSize: 12.5, color: '#555', marginBottom: 6 }}>📎 {f.orig_name || f.file_name}</div>
              <FileThumb url={`/uploads/${f.file_name}`} name={f.file_name} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// 2) 자산관리 — 마스터 목록 + 등록/수정 + 일괄수정 + 라벨출력 + 상세(이력)
// ══════════════════════════════════════════════════════════════
// 2026-08-30 — 자산관리 상단탭 8개 개편: 집기자산/전산자산/소프트웨어/장비자산/공구자산/
// 비품자산 6개 탭이 각자 완전히 독립된 등록/목록 화면으로 동작해야 한다는 요청(사용자가
// "탭마다 완전히 독립된 등록/목록 화면"을 명시적으로 선택)에 따라, 기존 통합 자산목록
// 화면(AssetListTab)을 asset_type으로 필터링/등록하는 방식으로 재사용한다. assetType이
// 주어지면 그 유형의 자산만 조회하고, 등록폼 저장 시에도 그 유형으로 자동 지정된다
// (사용자가 유형을 바꿀 수 없음 — 탭 자체가 유형을 결정).
function AssetListTab({ vendors, userList, settings, canWriteAssets, canDelete, canDownload, registerPrintExport, assetFormTrigger, consumeAssetFormTrigger, assetType, typeLabel, assetSearch, ledgerFilter }) {
  const [assets, setAssets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [msg, setMsg] = useState('');

  // 2026-08-31(6차) — 목록 화면 전면개편 요청("소프트웨어 목록과 위치 부서등을 삭제")에 따라
  // 위치/부서/상태 개별 필터를 없애고, 검색어는 AssetsPage 상단(탭 줄 우측)에서 내려주는
  // assetSearch 하나만 사용한다.
  // 2026-09(9차) 요청: "제외항목 포함" 토글 대신, 장부등록 여부(모두보기/장부등록/장부미등록)
  // 필터를 AssetsPage 검색창 옆으로 옮겨 ledgerFilter로 내려받는다.
  const load = () => {
    const params = {};
    if (assetSearch) params.q = assetSearch;
    if (assetType) params.asset_type = assetType;
    if (ledgerFilter && ledgerFilter !== 'all') params.ledger = ledgerFilter;
    API.get('/assets', { params }).then(r => setAssets(r.data)).catch(() => {});
  };
  useEffect(load, [assetSearch, assetType, ledgerFilter]); // eslint-disable-line
  useEffect(() => { if (!msg) return; const t = setTimeout(() => setMsg(''), 2500); return () => clearTimeout(t); }, [msg]);

  // 우측상단 고정 "자산등록" 버튼(다른 탭에 있을 때 눌러도 이 탭으로 전환된 뒤 등록폼이 열린다)
  // 2026-08-31(4차) — 버그수정: 유형탭 전환마다 이 컴포넌트가 리마운트되므로, 단순히
  // "assetFormTrigger가 0이 아니면 연다"였던 예전 방식은 등록 버튼을 한 번이라도 누른 뒤로는
  // 아무 탭이나 클릭해도 등록폼이 뜨는 버그였다. 부모(AssetsPage)가 들고 있는
  // consumeAssetFormTrigger로 "이 trigger 값을 이미 소비했는지" 확인해서, 실제로 등록 버튼을
  // 눌러 생긴 새 값일 때만 폼을 연다.
  useEffect(() => {
    if (consumeAssetFormTrigger && consumeAssetFormTrigger(assetFormTrigger)) {
      setEditData(null);
      setShowForm(true);
    }
  }, [assetFormTrigger]); // eslint-disable-line

  const { sorted, sortKey, sortDir, toggleSort } = useSort(assets, 'created_at');
  const statusOptions = settings?.status_options_parsed?.length ? settings.status_options_parsed : ['사용', '수리', '대여', '불용', '분실', '폐기', '보관'];

  const listTitle = typeLabel ? `${typeLabel} 목록` : '자산 목록';

  // 2026-08-31 — 자산목록 개편(요청사항 그대로) 반영: 구분/관리번호/자산명/상세규격/라이선스/
  // 구매일/구매금액(VAT포함)/사용부서/위치/상태/갱신·만료일/구매처/담당자 순으로 통일. 기존
  // "품목" 컬럼은 완전히 제거(품목분류 자체를 폐지한 요청에 따름).
  useEffect(() => {
    registerPrintExport({
      onPrint: () => printListWindow({
        menu: '자산관리', title: `🖥️ 자산관리 — ${listTitle}`,
        columns: [
          { label: '구분', value: a => a.division_label }, { label: '관리번호', value: a => a.asset_code }, { label: '자산명', value: a => a.asset_name },
          { label: '상세규격', value: a => a.spec }, { label: '라이선스', value: a => a.license },
          { label: '구매일', value: a => a.acquire_date }, { label: '구매금액(VAT포함)', value: a => moneyFmt(a.acquire_price) },
          { label: '사용부서', value: a => a.department }, { label: '위치', value: a => a.location_name }, { label: '상태', value: a => a.status },
          { label: '갱신/만료일', value: a => a.is_permanent ? '영구' : a.renew_expire_date }, { label: '구매처', value: a => a.vendor_name }, { label: '담당자', value: a => a.manager_name },
          { label: 'AS담당자', value: a => a.as_manager_name },
        ], rows: sorted, listName: `${listTitle}(${sorted.length}건)`,
      }),
      onExport: () => exportListXlsx({
        menu: '자산관리', filename: `IMS_${(typeLabel || '자산목록').replace(/\s/g, '')}_${new Date().toISOString().slice(0, 10)}.xlsx`, sheetName: typeLabel || '자산목록',
        columns: [
          { label: '구분', value: a => a.division_label, wch: 10 }, { label: '관리번호', value: a => a.asset_code, wch: 14 }, { label: '자산명', value: a => a.asset_name, wch: 20 },
          { label: '상세규격', value: a => a.spec, wch: 16 }, { label: '라이선스', value: a => a.license, wch: 14 },
          { label: '구매일', value: a => a.acquire_date, wch: 12 }, { label: '구매금액(VAT포함)', value: a => a.acquire_price || 0, wch: 14 },
          { label: '사용부서', value: a => a.department, wch: 10 }, { label: '위치', value: a => a.location_name, wch: 12 }, { label: '상태', value: a => a.status, wch: 8 },
          { label: '갱신/만료일', value: a => a.is_permanent ? '영구' : a.renew_expire_date, wch: 12 }, { label: '구매처', value: a => a.vendor_name, wch: 12 }, { label: '담당자', value: a => a.manager_name, wch: 10 },
          { label: 'AS담당자', value: a => a.as_manager_name, wch: 10 },
        ], rows: sorted,
      }),
    });
    return () => registerPrintExport(null);
  }, [sorted, listTitle]); // eslint-disable-line

  const openDetail = (asset) => setSelected(asset);
  const handleEdit = (asset) => { setEditData(asset); setShowForm(true); setSelected(null); };
  const handleDelete = async (id) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    await API.delete(`/assets/${id}`); setSelected(null); setMsg('삭제 완료!'); load();
  };

  return (
    <div>
      {/* 2026-08-31(6차) — 유형탭 목록 화면 전면개편: "○○ 목록" 제목과 위치/부서/상태 필터를
          모두 없앤다(검색은 AssetsPage 탭 줄 우측의 검색창 하나로 통일). */}
      <Section>
        {msg && <div style={{ background: '#d1fae5', color: '#065f46', padding: '8px 14px', borderRadius: 8, marginBottom: 12, fontSize: 13 }}>{msg}</div>}

        {sorted.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '50px 0', color: '#aaa', fontSize: 14 }}>등록된 자산이 없습니다.</div>
        ) : (
          <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, overflowX: 'auto', maxHeight: '70vh', overflowY: 'auto' }}>
            {/* 2026-09(9차) 요청: 선택 체크박스 열 제거, 구분 왼쪽에 장부등록 열 추가, 관리번호와
                자산명 사이에 자산등록일(구매일과 동일값) 열 추가. 2026-08-31(3차)부터의 표 헤더
                상단고정(position: sticky)은 그대로 유지. */}
            <div style={{ display: 'grid', gridTemplateColumns: '84px 64px 110px 100px 1fr 130px 260px 120px 120px 130px 200px 120px 64px', ...th, position: 'sticky', top: 0, zIndex: 2, background: th.background || '#f8fafc' }}>
              {/* 2026-09-13(11차) 요청: 목록 전체 컬럼에 클릭정렬 기능 적용 (라벨출력 제외 - 동작버튼 열) */}
              <Sortable label="장부등록" sortKey="list_exclude" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
              <Sortable label="구분" sortKey="division_label" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
              <Sortable label="관리번호" sortKey="asset_code" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
              <Sortable label="자산등록일" sortKey="acquire_date" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
              <Sortable label="자산명" sortKey="asset_name" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
              {/* 2026-09(3차) 요청: 자산명 옆에 괄호로 붙여 표시하던 모델명을 별도 열로 분리 */}
              <Sortable label="모델명" sortKey="model_name" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
              <Sortable label="상세규격" sortKey="spec" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
              <Sortable label="사용부서" sortKey="department" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
              <Sortable label="구매일" sortKey="acquire_date" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
              <Sortable label="구매처" sortKey="vendor_name" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
              <Sortable label="AS담당자" sortKey="as_manager_name" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
              <Sortable label="갱신/만료일" sortKey="renew_expire_date" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
              <span>라벨출력</span>
            </div>
            {sorted.map((a, i) => (
              <div key={a.id} style={{ display: 'grid', gridTemplateColumns: '84px 64px 110px 100px 1fr 130px 260px 120px 120px 130px 200px 120px 64px', ...td, cursor: 'pointer', borderTop: i > 0 ? undefined : 'none' }}
                onClick={() => openDetail(a)}>
                {/* 2026-09(9차) 요청: list_exclude=0/null이면 "장부등록", 1이면 "장부미등록" */}
                <span style={{ color: a.list_exclude ? '#dc2626' : '#0F6E56', fontWeight: 600, fontSize: 12.5 }}>{a.list_exclude ? '장부미등록' : '장부등록'}</span>
                <span>{a.division_label || '-'}</span>
                <span>{a.asset_code || '-'}</span>
                {/* 자산등록일은 구매일로 자동등록된다 */}
                <span>{a.acquire_date || '-'}</span>
                {/* 2026-09(4차) 요청: 자산명 가운데 정렬 (기존 왼쪽정렬에서 변경) */}
                <span style={{ fontWeight: 600 }}>{a.asset_name}</span>
                <span>{a.model_name || '-'}</span>
                <span>{a.spec || '-'}{a.spec_attach_count > 0 && <span title={`첨부 ${a.spec_attach_count}개`} style={{ marginLeft: 3, color: '#185FA5' }}>📎{a.spec_attach_count}</span>}</span>
                <span>{a.department || '-'}</span>
                <span>{a.acquire_date || '-'}</span>
                <span>{a.vendor_name || '-'}</span>
                <span>{a.as_manager_name || '-'}{a.as_manager_contact ? ` / ${a.as_manager_contact}` : ''}</span>
                {/* 2026-09(6차) 요청: 영구 체크된 자산은 목록 갱신/만료일 칸에 '영구' 표시 */}
                <span>{a.is_permanent ? '영구' : (a.renew_expire_date || '-')}</span>
                <span onClick={e => e.stopPropagation()}>
                  {canDownload && <button onClick={() => printPdfFromApi(`/assets/label/pdf?ids=${a.id}`)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13 }} title="라벨출력">🏷️</button>}
                </span>
              </div>
            ))}
          </div>
        )}
      </Section>

      {selected && (
        <AssetDetailModal asset={selected} vendors={vendors} userList={userList} statusOptions={statusOptions}
          canWrite={canWriteAssets} canDelete={canDelete}
          onClose={() => setSelected(null)} onEdit={handleEdit} onDelete={handleDelete} onChanged={load} />
      )}

      {showForm && (
        <AssetForm editData={editData} vendors={vendors}
          userList={userList} settings={settings} statusOptions={statusOptions} assetType={assetType} typeLabel={typeLabel}
          onClose={() => { setShowForm(false); setEditData(null); load(); }}
          onSaved={() => { setShowForm(false); setEditData(null); load(); setMsg(editData ? '수정 완료!' : '등록 완료!'); }} />
      )}

    </div>
  );
}

const EXTRA_FIELD_COUNT = 8;
// 2026-08-30 — 유형별 탭(집기자산/전산자산/소프트웨어/장비자산/공구자산/비품자산)에서 열린
// 등록폼은 assetType prop으로 자산유형이 이미 정해져 있으므로 사용자가 고를 필요가 없다
// (탭 자체가 유형을 뜻함). editData?.asset_type이 있으면(수정) 그 값을 우선한다.
function AssetForm({ editData, userList, settings, statusOptions, assetType, typeLabel, onClose, onSaved }) {
  // 2026-08-31 — 자산목록 개편 요청 반영: 품목분류(category_id) 입력란 완전 제거,
  // 라이선스/갱신·만료일 필드 추가, 상세규격/라이선스 각각 다중 첨부파일 지원.
  // 2026-08-31(3차) — 기준정보(위치분류/거래처 관리) 폐지에 따라 위치/구매처를 드롭다운
  // 선택(location_id/vendor_id) 대신 직접입력(텍스트: location/vendor_name)으로 전환.
  // AS담당자(as_manager_id)는 담당자/사용자와 동일하게 내부 직원 선택 필드로 추가.
  // 2026-08-31(6차) — 등록폼 전면개편(요청 확정사항):
  //  - 관리번호: 탭별 접두어 자동생성 + 미할당 번호 순번 배정(서버가 저장 시 자동 부여) —
  //    사용자가 고르는 값이 아니라 읽기전용 표시만 한다.
  //  - 사용부서: 회원 datalist가 아니라 조직도(부서명) 드롭다운으로 전환.
  //  - 위치/RFID코드: 요청에 따라 완전히 삭제(기존 데이터는 DB/목록에 남지만 입력경로는 없앰).
  //  - 연락처: AS담당자(내부직원 선택)와 별개로 직접입력하는 새 필드(as_manager_contact).
  // 2026-09-01 — "구매처 옆 담당자는 (내부직원) 선택이 아니라 직급+이름 직접입력" 요청에 따라
  // manager_id(내부직원 select) → manager_name(텍스트, 예: "과장 홍길동")으로 전환.
  // 2026-09-01(2차) — "AS담당자도 직접입력" + "구매처 담당자와 동일 체크박스" + "구매처/담당자 옆
  // 연락처 추가입력" 요청에 따라 manager_contact 필드를 신설하고, as_manager_id(내부직원 select)를
  // as_manager_name(텍스트)으로 전환한다.
  const [form, setForm] = useState({
    department: editData?.department || '', manager_name: editData?.manager_name || '', manager_contact: editData?.manager_contact || '', user_id: editData?.user_id || '',
    as_manager_name: editData?.as_manager_name || '', as_manager_contact: editData?.as_manager_contact || '',
    asset_name: editData?.asset_name || '', model_name: editData?.model_name || '', spec: editData?.spec || '',
    // 2026-09(6차) 요청: 상세규격 옆 수량 입력 추가
    qty: editData?.qty || '',
    license: editData?.license || '', renew_expire_date: editData?.renew_expire_date || '',
    serial_no: editData?.serial_no || '',
    acquire_date: editData?.acquire_date || '', acquire_price: editData?.acquire_price ? formatComma(String(editData.acquire_price)) : '',
    residual_value: editData?.residual_value ? formatComma(String(editData.residual_value)) : '',
    status: editData?.status || statusOptions[0] || '사용', vendor_name: editData?.vendor_name || '',
    depreciation_exempt: !!editData?.depreciation_exempt, note: editData?.note || '',
    asset_type: editData?.asset_type || assetType || '',
    ...Object.fromEntries(Array.from({ length: EXTRA_FIELD_COUNT }, (_, i) => [`extra${i + 1}`, editData?.[`extra${i + 1}`] || ''])),
  });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  // 2026-09(5차) 요청: 자산 사진 삭제 기능 — 수정모드에서 이미 등록된 사진을 삭제 표시만
  // 해두었다가(즉시 삭제하지 않고 "저장" 시점에 반영), 저장 시 remove_image=1을 함께 전송한다.
  // 새 사진을 선택하면 삭제표시는 자동 취소(새 사진이 기존 삭제 의도를 대체).
  const [removeImage, setRemoveImage] = useState(false);
  // 2026-09(5차) 요청: 신규 자산등록을 "등록"→"완료" 2단계가 아니라 한 번의 클릭으로 끝내기 위해
  // createdId(중간 저장 상태) 방식을 폐지하고, 첨부파일은 자산이 생성되기 전까지 File 객체로만
  // 로컬에 보관해두었다가(specFiles/licenseFiles) submit() 한 번에 자산 생성 + 첨부업로드까지 처리한다.
  const [specFiles, setSpecFiles] = useState([]);
  const [licenseFiles, setLicenseFiles] = useState([]);
  // 2026-09(6차) 요청: 갱신/만료일 옆 "영구" 체크박스 — 체크되면 갱신/만료일을 입력하지 않는다.
  const [isPermanent, setIsPermanent] = useState(!!editData?.is_permanent);
  // 2026-09(9차) 요청: "장부등록제외" 체크박스(구 "자산목록 제외") — 체크되면 실제로는 보유
  // 중이지만 회계장부에는 등록되지 않은 자산임을 뜻한다(완전 삭제 아님, DB에는 그대로 남음).
  // 필드명(list_exclude)은 유지하되 목록에서는 더 이상 숨기지 않고 "장부등록/장부미등록"
  // 컬럼과 필터로 표시한다.
  const [listExclude, setListExclude] = useState(!!editData?.list_exclude);
  const ch = e => setForm({ ...form, [e.target.name]: e.target.value });
  const extraLabels = settings?.extra_labels_parsed || [];

  // 2026-09(16차) 요청 — "각 탭에서 잔존가치를 구입년월일과 감가설정에서 설정된 비율에 맞게
  // 자동으로 잔존가액을 적용". 이 자산유형(asset_type) 탭에 연결된 기본 감가상각계정(관리항목/
  // 감가설정에서 지정)을 찾아, 구매일+구매금액이 입력될 때마다 잔존가액을 자동으로 다시 계산해
  // 채워 넣는다. 기본계정이 아직 연결 안 된 유형(예: 감가설정 미지정)은 기존처럼 수기입력을
  // 그대로 둔다.
  const [depAccount, setDepAccount] = useState(null);
  useEffect(() => {
    Promise.all([API.get('/assets/menu-tabs'), API.get('/assets/depreciation/accounts')]).then(([tabsRes, accRes]) => {
      const tabKey = form.asset_type || assetType;
      const tab = (tabsRes.data || []).find(t => t.tab_key === tabKey);
      const acc = tab?.default_dep_account_id ? (accRes.data || []).find(a => a.id === tab.default_dep_account_id) : null;
      setDepAccount(acc || null);
      // 2026-09-13 추가 — 감가설정 모달에서 탭(관리항목) 단위로 "감가제외"를 지정하면, 그
      // 유형으로 신규 등록되는 자산은 기본값으로 감가상각 제외가 체크되도록 한다(수정모드는
      // 기존 저장값을 존중하므로 건드리지 않는다).
      if (!editData && tab) setForm(f => ({ ...f, depreciation_exempt: !!tab.dep_exempt }));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.asset_type]);

  const clientComputeResidual = (acquireDateStr, acquirePriceStr, account) => {
    if (!account || !acquireDateStr) return null;
    const price = Number(onlyDigits(acquirePriceStr)) || 0;
    if (price <= 0) return null;
    const acq = new Date(acquireDateStr);
    if (isNaN(acq.getTime())) return null;
    const now = new Date();
    const usefulLife = Number(account.useful_life || 5);
    const totalMonths = usefulLife * 12;
    let elapsed = (now.getFullYear() - acq.getFullYear()) * 12 + (now.getMonth() - acq.getMonth()) + 1;
    elapsed = Math.max(0, Math.min(elapsed, totalMonths));
    if (account.method === '정률법') {
      const rate = account.dep_rate ? Number(account.dep_rate) / 100 : (1 / usefulLife) * 2;
      let bookValue = price;
      for (let m = 0; m < elapsed; m++) {
        const monthlyDep = Math.max(0, bookValue) * (rate / 12);
        bookValue -= monthlyDep;
        if (bookValue <= 0) { bookValue = 0; break; }
      }
      return Math.round(bookValue);
    }
    const monthlyDep = price / totalMonths;
    const accumDep = Math.min(monthlyDep * elapsed, price);
    return Math.round(price - accumDep);
  };
  const autoResidual = depAccount ? clientComputeResidual(form.acquire_date, form.acquire_price, depAccount) : null;
  // 구매일/구매금액/적용계정이 바뀔 때마다 잔존가액을 자동으로 다시 채운다(요청사항 그대로 —
  // 수기 입력란은 남겨두되 값 자체는 항상 자동계산 결과를 따라간다).
  useEffect(() => {
    if (autoResidual == null) return;
    setForm(f => (formatComma(String(autoResidual)) === f.residual_value ? f : { ...f, residual_value: formatComma(String(autoResidual)) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoResidual]);

  // 2026-09-01(2차) — "AS담당자 옆에 구매처 담당자와 동일 체크박스 추가 (체크시 구매처 담당자 및
  // 연락처와 동일하게 입력됨)" 요청. 체크된 동안은 담당자/연락처가 바뀔 때마다 AS담당자에도
  // 계속 동일하게 반영되도록 동기화한다.
  const [sameAsVendorManager, setSameAsVendorManager] = useState(false);
  useEffect(() => {
    if (sameAsVendorManager) {
      setForm(f => ({ ...f, as_manager_name: f.manager_name, as_manager_contact: f.manager_contact }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sameAsVendorManager, form.manager_name, form.manager_contact]);

  // 2026-08-31(6차) — 사용부서 드롭다운을 조직도(부서명) 기준으로 채운다(요청: "사용부서
  // (조직도에서 부서명을 선택)"). 회원 목록에서 뽑던 기존 datalist 방식은 폐지.
  const [departments, setDepartments] = useState([]);
  useEffect(() => {
    API.get('/org/public').then(r => setDepartments(r.data?.departments || [])).catch(() => {});
  }, []);

  const submit = async () => {
    if (!form.asset_name.trim()) return alert('자산명을 입력해주세요.');
    if (!form.department) return alert('사용부서를 선택해주세요.');
    setLoading(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => {
      if (k === 'acquire_price' || k === 'residual_value') fd.append(k, onlyDigits(v));
      else if (k === 'depreciation_exempt') fd.append(k, v ? '1' : '0');
      else fd.append(k, v ?? '');
    });
    // 2026-09(6차) 요청: 영구 체크박스 — 체크되면 서버에서 갱신/만료일을 비워 저장한다.
    fd.append('is_permanent', isPermanent ? '1' : '0');
    // 2026-09(7차) 요청: 자산목록 제외 체크박스
    fd.append('list_exclude', listExclude ? '1' : '0');
    if (file) fd.append('image', file);
    try {
      if (editData) {
        // 2026-09(5차) 요청: 사진 삭제 표시가 있으면 저장 시 함께 반영(새 사진이 있으면 그쪽이 우선)
        fd.append('remove_image', removeImage ? '1' : '0');
        await API.put(`/assets/${editData.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        // 2026-09(5차) 요청: 자산 생성 후, 등록화면에서 미리 담아둔 상세규격/라이선스 첨부파일을
        // 곧바로 이어서 업로드해 "자산 등록" 버튼 한 번으로 전체 등록이 끝나도록 한다.
        const res = await API.post('/assets', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        const newId = res.data.id;
        const uploads = [];
        if (specFiles.length) {
          const fdS = new FormData();
          fdS.append('field_type', 'spec');
          specFiles.forEach(f => fdS.append('files', f));
          uploads.push(API.post(`/assets/${newId}/attachments`, fdS, { headers: { 'Content-Type': 'multipart/form-data' } }));
        }
        if (licenseFiles.length) {
          const fdL = new FormData();
          fdL.append('field_type', 'license');
          licenseFiles.forEach(f => fdL.append('files', f));
          uploads.push(API.post(`/assets/${newId}/attachments`, fdL, { headers: { 'Content-Type': 'multipart/form-data' } }));
        }
        if (uploads.length) await Promise.all(uploads);
      }
      onSaved();
    } catch (e) { alert(e.response?.data?.message || '오류가 발생했습니다.'); } finally { setLoading(false); }
  };

  return (
    <div style={overlayStyle}>
      <div style={{ ...modalStyle, width: 620 }} onClick={e => e.stopPropagation()}>
        {/* 2026-09(9차) 요청: "자산목록 제외" 체크박스를 하단에서 상단 닫기버튼 왼쪽으로 이동하고
            "장부등록제외"로 개칭 — 실제 보유 자산이지만 회계장부에는 등록되지 않은 자산을 뜻한다
            (기존 list_exclude 필드/의미를 그대로 재사용). */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <h3 style={{ fontSize: 17, fontWeight: 600, margin: 0 }}>{`${ASSET_TYPE_META[form.asset_type]?.icon || ''} ${ASSET_TYPE_META[form.asset_type]?.label || typeLabel || ''} ${editData ? '수정' : '등록'}`.trim()}</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#555', cursor: 'pointer' }}>
              <input type="checkbox" checked={listExclude} onChange={e => setListExclude(e.target.checked)} />
              장부등록제외
            </label>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#999' }}>✕</button>
          </div>
        </div>

        {/* 1행 — 관리번호(자동생성, 읽기전용) + 자산명 */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label style={labelStyle}>관리번호</label>
            <input value={editData?.asset_code || '(저장 시 자동 생성)'} disabled style={{ ...formInput, background: '#f8fafc', color: '#888' }} />
          </div>
          <div><label style={labelStyle}>자산명 *</label>
            <input name="asset_name" value={form.asset_name} onChange={ch} style={formInput} />
          </div>
        </div>

        {/* 2행 — 모델명 + 시리얼번호 */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label style={labelStyle}>모델명</label><input name="model_name" value={form.model_name} onChange={ch} style={formInput} /></div>
          <div><label style={labelStyle}>시리얼번호</label><input name="serial_no" value={form.serial_no} onChange={ch} style={formInput} /></div>
        </div>

        {/* 3행 — 상세규격 + 수량 */}
        {/* 2026-09(6차) 요청: 상세규격 옆에 수량 입력란 추가 */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px', gap: 10 }}>
          <div><label style={labelStyle}>상세규격</label><input name="spec" value={form.spec} onChange={ch} style={formInput} /></div>
          <div><label style={labelStyle}>수량</label><input name="qty" value={form.qty} onChange={ch} style={formInput} placeholder="예: 2" /></div>
        </div>
        {/* 2026-09(5차) 요청: 신규등록 화면에서는 자산이 아직 없어 첨부파일을 즉시 업로드할 수
            없으므로 PendingAttachmentsPicker로 로컬에 담아두었다가 "자산 등록" 클릭 시 함께
            업로드한다. 수정화면(editData 존재)에서는 기존처럼 AssetAttachmentsPanel로 즉시 업로드. */}
        {editData ? (
          <AssetAttachmentsPanel assetId={editData.id} fieldType="spec" canWrite />
        ) : (
          <PendingAttachmentsPicker
            files={specFiles}
            onAdd={fs => setSpecFiles(prev => [...prev, ...fs])}
            onRemove={i => setSpecFiles(prev => prev.filter((_, idx) => idx !== i))}
          />
        )}

        {/* 4행 — 라이선스 */}
        <label style={{ ...labelStyle, marginTop: 10 }}>라이선스</label>
        <input name="license" value={form.license} onChange={ch} style={formInput} placeholder="예: MS Office 정품 라이선스 키" />
        {editData ? (
          <AssetAttachmentsPanel assetId={editData.id} fieldType="license" canWrite />
        ) : (
          <PendingAttachmentsPicker
            files={licenseFiles}
            onAdd={fs => setLicenseFiles(prev => [...prev, ...fs])}
            onRemove={i => setLicenseFiles(prev => prev.filter((_, idx) => idx !== i))}
          />
        )}

        {/* 5행 — 사용부서(조직도) + 사용자 + 상태 */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginTop: 10 }}>
          <div><label style={labelStyle}>사용부서 *</label>
            <select name="department" value={form.department} onChange={ch} style={formInput}>
              <option value="">선택</option>
              {departments.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div><label style={labelStyle}>사용자</label>
            <select name="user_id" value={form.user_id} onChange={ch} style={formInput}>
              <option value="">선택안함</option>{userList.map(u => <option key={u.id} value={u.id}>{fmtUser(u)}</option>)}
            </select>
          </div>
          <div><label style={labelStyle}>상태</label>
            <select name="status" value={form.status} onChange={ch} style={formInput}>{statusOptions.map(s => <option key={s} value={s}>{s}</option>)}</select>
          </div>
        </div>

        {/* 6행 — 구매처 + 담당자 + 연락처 */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <div><label style={labelStyle}>구매처</label><input name="vendor_name" value={form.vendor_name} onChange={ch} style={formInput} /></div>
          <div><label style={labelStyle}>담당자</label>
            <input name="manager_name" value={form.manager_name} onChange={ch} style={formInput} placeholder="예: 과장 홍길동" />
          </div>
          <div><label style={labelStyle}>연락처</label>
            <input name="manager_contact" value={form.manager_contact} onChange={ch} style={formInput} placeholder="예: 010-1234-5678" />
          </div>
        </div>

        {/* 7행 — 구매일 + 갱신/만료일(+영구 체크박스) */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label style={labelStyle}>구매일</label><input type="date" name="acquire_date" value={form.acquire_date} onChange={ch} style={formInput} /></div>
          <div>
            <label style={labelStyle}>
              갱신/만료일
              {/* 2026-09(6차) 요청: 영구 체크시 갱신/만료일을 입력하지 않는다(체크되면 서버 저장 시 null 처리) */}
              <label style={{ marginLeft: 8, fontWeight: 400, fontSize: 12, color: '#666', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={isPermanent}
                  onChange={e => { setIsPermanent(e.target.checked); if (e.target.checked) setForm(f => ({ ...f, renew_expire_date: '' })); }}
                  style={{ marginRight: 4, verticalAlign: 'middle' }}
                />
                영구
              </label>
            </label>
            <input type="date" name="renew_expire_date" value={form.renew_expire_date} onChange={ch} style={formInput} disabled={isPermanent} />
          </div>
        </div>

        {/* 8행 — 구매금액 + 잔존가액 */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label style={labelStyle}>구매금액(VAT포함)</label><input name="acquire_price" value={form.acquire_price} onChange={e => setForm({ ...form, acquire_price: formatComma(e.target.value) })} inputMode="numeric" style={formInput} /></div>
          <div>
            <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>잔존가액{depAccount ? ' (자동계산)' : ''}</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontWeight: 400, fontSize: 12, color: '#666', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.depreciation_exempt} onChange={e => setForm({ ...form, depreciation_exempt: e.target.checked })} />
                감가상각 제외
              </span>
            </label>
            <input name="residual_value" value={form.residual_value} onChange={e => setForm({ ...form, residual_value: formatComma(e.target.value) })}
              inputMode="numeric" style={{ ...formInput, background: depAccount ? '#f8fafc' : '#fff' }} readOnly={!!depAccount} />
            {depAccount ? (
              <div style={{ fontSize: 11, color: '#999', marginTop: -8, marginBottom: 10 }}>
                {depAccount.method}{depAccount.useful_life ? ` · 내용연수 ${depAccount.useful_life}년` : ''} 기준 구매일로부터 자동계산됩니다.
              </div>
            ) : (
              <div style={{ fontSize: 11, color: '#c2410c', marginTop: -8, marginBottom: 10 }}>
                이 자산유형에 감가설정이 연결되어 있지 않아 수기입력값이 그대로 쓰입니다.
              </div>
            )}
          </div>
        </div>

        {/* 9행 — AS담당자(직접입력) + 연락처(직접입력) + 구매처 담당자와 동일 체크박스 */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div>
            <label style={labelStyle}>
              AS담당자
              <label style={{ marginLeft: 8, fontWeight: 400, fontSize: 12, color: '#666', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={sameAsVendorManager}
                  onChange={e => setSameAsVendorManager(e.target.checked)}
                  style={{ marginRight: 4, verticalAlign: 'middle' }}
                />
                구매처 담당자와 동일
              </label>
            </label>
            {/* 2026-09 요청: "AS담당자에 직급과 이름 앞에 회사명도 추가" — 직접입력 필드 자체는
                그대로 두고(구매처 담당자와 동일한 방식), 회사명까지 함께 적도록 예시 문구만 보강했다. */}
            <input name="as_manager_name" value={form.as_manager_name} onChange={ch} style={formInput} disabled={sameAsVendorManager} placeholder="예: OO전자 과장 홍길동" />
          </div>
          <div><label style={labelStyle}>연락처</label>
            <input name="as_manager_contact" value={form.as_manager_contact} onChange={ch} style={formInput} disabled={sameAsVendorManager} placeholder="예: 010-1234-5678" />
          </div>
        </div>

        {extraLabels.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {extraLabels.map((label, i) => (
              <div key={i}><label style={labelStyle}>{label}</label><input name={`extra${i + 1}`} value={form[`extra${i + 1}`]} onChange={ch} style={formInput} /></div>
            ))}
          </div>
        )}

        <label style={labelStyle}>비고</label>
        <textarea name="note" value={form.note} onChange={ch} rows={2} style={{ ...formInput, resize: 'vertical' }} />

        {/* 2026-09(5차) 요청: 자산 사진 삭제 기능 — 이미 등록된 사진이 있으면 썸네일과 삭제
            버튼을 보여주고, 삭제 표시 상태에서는 안내문구+취소 링크를 노출한다. 실제 삭제는
            "저장"을 눌러야 반영된다(remove_image 플래그로 서버 전송). */}
        <label style={labelStyle}>자산 사진 {editData?.image_path && !removeImage && <span style={{ color: '#185FA5', fontSize: 12 }}>(등록됨)</span>}</label>
        {editData?.image_path && !removeImage && !file && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <img src={`/uploads/${editData.image_path}`} alt="자산 사진" style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 6, border: '1px solid #e2e8f0' }} />
            <button type="button" onClick={() => setRemoveImage(true)} style={{ background: 'none', border: '1px solid #dc2626', color: '#dc2626', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}>🗑 사진 삭제</button>
          </div>
        )}
        {removeImage && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: 12.5, color: '#dc2626' }}>
            사진이 삭제됩니다 (저장 시 반영)
            <button type="button" onClick={() => setRemoveImage(false)} style={{ background: 'none', border: 'none', color: '#185FA5', cursor: 'pointer', fontSize: 12, textDecoration: 'underline' }}>취소</button>
          </div>
        )}
        <FileDropZone fileName={file?.name} onFile={f => { setFile(f); setRemoveImage(false); }} />

        <button onClick={submit} disabled={loading} style={{ ...btn(loading ? '#aaa' : '#185FA5'), width: '100%', padding: 12, marginTop: 8 }}>
          {loading ? '저장 중...' : (editData ? '수정 완료' : '자산 등록')}
        </button>
      </div>
    </div>
  );
}

// 2026-08-31 신규 — 상세규격/라이선스 다중 첨부파일 UI. asset_id가 있어야(즉 자산이 이미
// 등록되어 있어야) 업로드가 가능하다(백엔드 asset_attachments가 asset_id FK를 요구).
// 2026-09(5차) 요청: 상세화면(AssetDetailModal)의 "라이선스" 항목에서는 조회/다운로드/삭제만
// 가능하게 하고, 드래그앤드롭 업로드 영역은 노출하지 않는다(첨부는 이제 자산등록 시점에
// 완료되는 것이 정상 흐름이므로). allowUpload=false로 넘기면 업로드 드롭존만 숨긴다 — 다른
// 화면(자산등록/수정 폼)에서는 기존처럼 그대로 사용(기본값 true).
function AssetAttachmentsPanel({ assetId, fieldType, canWrite, allowUpload = true }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => {
    if (!assetId) { setFiles([]); return; }
    API.get(`/assets/${assetId}/attachments`, { params: { field_type: fieldType } }).then(r => setFiles(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [assetId, fieldType]); // eslint-disable-line

  const doUpload = (fileList) => {
    if (!assetId || !fileList || !fileList.length) return;
    setBusy(true);
    const fd = new FormData();
    fd.append('field_type', fieldType);
    Array.from(fileList).forEach(f => fd.append('files', f));
    API.post(`/assets/${assetId}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then(load).catch(e => alert(e.response?.data?.message || '첨부파일 업로드 중 오류가 발생했습니다.')).finally(() => setBusy(false));
  };

  const remove = (id) => {
    if (!window.confirm('첨부파일을 삭제하시겠습니까?')) return;
    API.delete(`/assets/attachments/${id}`).then(load).catch(() => alert('삭제 중 오류가 발생했습니다.'));
  };

  if (!assetId) {
    return <div style={{ fontSize: 12, color: '#aaa', marginTop: 2, marginBottom: 4 }}>자산을 먼저 등록해야 첨부파일을 추가할 수 있습니다.</div>;
  }
  return (
    <div style={{ marginTop: 4, marginBottom: 4 }}>
      {files.map(f => (
        <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, marginBottom: 4 }}>
          <a href={`/uploads/${f.file_name}`} target="_blank" rel="noreferrer" style={{ color: '#185FA5', textDecoration: 'none' }}>📎 {f.orig_name || f.file_name}</a>
          {canWrite && <button type="button" onClick={() => remove(f.id)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 12 }}>✕</button>}
        </div>
      ))}
      {canWrite && allowUpload && <FileDropZone multiple onFiles={doUpload} fileNames={busy ? ['업로드 중...'] : []} />}
    </div>
  );
}

// 2026-09(5차) 요청 — 신규 자산등록 화면에서 상세규격/라이선스 첨부파일을 자산이 실제로
// 생성되기 전에 미리 선택해둘 수 있게 하는 경량 컴포넌트. AssetAttachmentsPanel과 달리 asset_id가
// 없어도 동작해야 하므로 서버에 즉시 업로드하지 않고 File 객체를 로컬 상태에만 담아두고,
// 실제 업로드는 자산등록 제출(submit) 시점에 asset_id가 생기자마자 한꺼번에 진행한다
// (기존에는 "등록"→"완료" 2단계로 나눠야만 첨부가 가능했던 것을 한 번의 클릭으로 끝내기 위함).
function PendingAttachmentsPicker({ files, onAdd, onRemove }) {
  return (
    <div style={{ marginTop: 4, marginBottom: 4 }}>
      {files.map((f, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, marginBottom: 4 }}>
          <span style={{ color: '#185FA5' }}>📎 {f.name}</span>
          <button type="button" onClick={() => onRemove(i)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 12 }}>✕</button>
        </div>
      ))}
      <FileDropZone multiple onFiles={onAdd} />
    </div>
  );
}

function BulkEditModal({ ids, userList, statusOptions, onClose, onSaved }) {
  const [enabled, setEnabled] = useState({});
  const [fields, setFields] = useState({});
  const [loading, setLoading] = useState(false);
  // 2026-08-31(3차) — 품목분류(category_id)는 이미 폐지되어 있었고, 위치/구매처도
  // 기준정보(위치분류/거래처 관리) 폐지에 따라 select→text(직접입력)로 전환.
  // 2026-09-01(2차) — AS담당자(as_manager_id select)도 담당자와 동일하게 text(직접입력)로 전환.
  const FIELD_DEFS = [
    ['location', '위치', 'text', null],
    ['department', '부서', 'text', null],
    ['manager_name', '담당자', 'text', null],
    ['manager_contact', '담당자 연락처', 'text', null],
    ['user_id', '사용자', 'select', userList.map(u => [u.id, fmtUser(u)])],
    ['as_manager_name', 'AS담당자', 'text', null],
    ['status', '상태', 'select', statusOptions.map(s => [s, s])],
    ['vendor_name', '구매처', 'text', null],
  ];
  const toggle = (key) => setEnabled(e => ({ ...e, [key]: !e[key] }));

  const submit = async () => {
    const activeFields = {};
    Object.keys(enabled).forEach(k => { if (enabled[k] && fields[k] !== undefined) activeFields[k] = fields[k]; });
    if (Object.keys(activeFields).length === 0) return alert('변경할 항목을 선택해주세요.');
    setLoading(true);
    try { await API.put('/assets/bulk', { ids, fields: activeFields }); onSaved(); }
    catch (e) { alert(e.response?.data?.message || '오류가 발생했습니다.'); } finally { setLoading(false); }
  };

  return (
    <div style={overlayStyle}>
      <div style={{ ...modalStyle, width: 480 }} onClick={e => e.stopPropagation()}>
        <ModalHeader title={`일괄수정 (${ids.length}건)`} onClose={onClose} />
        <div style={{ fontSize: 12, color: '#888', marginBottom: 14 }}>체크한 항목만 선택된 모든 자산에 동일하게 반영됩니다.</div>
        {FIELD_DEFS.map(([key, label, type, options]) => (
          <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <input type="checkbox" checked={!!enabled[key]} onChange={() => toggle(key)} />
            <span style={{ width: 70, fontSize: 13, color: '#555' }}>{label}</span>
            {type === 'select' ? (
              <select disabled={!enabled[key]} value={fields[key] || ''} onChange={e => setFields(f => ({ ...f, [key]: e.target.value }))}
                style={{ ...input, flex: 1, padding: '7px 10px' }}>
                <option value="">선택</option>{options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            ) : (
              <input disabled={!enabled[key]} value={fields[key] || ''} onChange={e => setFields(f => ({ ...f, [key]: e.target.value }))}
                style={{ ...input, flex: 1, padding: '7px 10px' }} />
            )}
          </div>
        ))}
        <button onClick={submit} disabled={loading} style={{ ...btn(loading ? '#aaa' : '#185FA5'), width: '100%', padding: 12, marginTop: 10 }}>
          {loading ? '적용 중...' : '일괄수정 적용'}
        </button>
      </div>
    </div>
  );
}

// ── 자산 상세 모달 (상세정보/변경이력/수리내역/대여내역/메모) ─────────────
function AssetDetailModal({ asset, vendors, userList, statusOptions, canWrite, canDelete, onClose, onEdit, onDelete, onChanged }) {
  const [card, setCard] = useState(null);
  const [sub, setSub] = useState('info');
  // 2026-09(3차) 요청: 헤더의 사진/라이선스 미리보기 모달 + 인쇄 진행상태.
  // card가 null인 첫 렌더에서도 훅 호출 순서가 매 렌더 동일해야 하므로, 아래 "if (!card) return null"
  // 보다 반드시 앞에 선언한다.
  const [previewModal, setPreviewModal] = useState(null); // { type: 'photo' } | { type: 'license', files }
  const [printing, setPrinting] = useState(false);
  const load = () => API.get(`/assets/${asset.id}/card`).then(r => setCard(r.data)).catch(() => {});
  // 2026-08-31(4차) — load()가 프라미스 체인을 그대로 반환(암시적 return)하는 화살표
  // 함수라서, 이걸 useEffect(load, deps)처럼 콜백 자체로 넘기면 언마운트 시 리액트가 그
  // 반환된 프라미스를 "정리 함수"로 착각해 그대로 호출하면서 "TypeError: X is not a
  // function"이 터진다(자산관리 탭을 여러 번 전환하면 흰 화면이 뜨던 원인). 콜백을
  // 블록 바디로 감싸 undefined를 반환하게 해서 이 문제를 없앤다.
  useEffect(() => { load(); }, [asset.id]); // eslint-disable-line

  if (!card) return null;
  const a = card.asset;

  // 2026-09(3차) 요청: "라이선스를 클릭하면 모달창을 통하여 미리보기" — 라이선스는 다중첨부라
  // 버튼 클릭 시점에 목록을 받아와 모달에 넘긴다(정보탭에 이미 노출 중인 목록과 동일 API).
  const openLicensePreview = async () => {
    try {
      const r = await API.get(`/assets/${a.id}/attachments`, { params: { field_type: 'license' } });
      setPreviewModal({ type: 'license', files: r.data || [] });
    } catch (e) { alert('라이선스 파일을 불러오지 못했습니다.'); }
  };

  // 2026-09(3차) 요청: "인쇄버튼을 누르면 입력된 정보와 라이선스, 사진을 같이 출력" — 팝업
  // 차단을 피하기 위해 클릭 시점(동기)에 빈 창을 먼저 열어두고, 라이선스 첨부 목록을 받아온
  // 뒤 그 창에 내용을 채워 인쇄한다(renderAssetPrintWindow, 파일 상단 공용 함수).
  const handlePrintAll = async () => {
    const pw = window.open('', '_blank', 'width=900,height=1000');
    if (!pw) { alert('팝업이 차단되어 인쇄창을 열 수 없습니다. 팝업 차단을 해제해주세요.'); return; }
    pw.document.write('<html><body style="font-family:sans-serif;padding:40px;color:#888;">인쇄 준비 중...</body></html>');
    setPrinting(true);
    try {
      let licenseFiles = [];
      if (a.license_attach_count > 0) {
        const r = await API.get(`/assets/${a.id}/attachments`, { params: { field_type: 'license' } });
        licenseFiles = r.data || [];
      }
      renderAssetPrintWindow(pw, a, licenseFiles);
    } catch (e) {
      pw.close();
      alert('인쇄 중 오류가 발생했습니다.');
    } finally { setPrinting(false); }
  };

  return (
    <>
    <div style={overlayStyle}>
      <div style={{ ...modalStyle, width: 640 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
          <div>
            {/* 2026-09 요청: 좌측 상단에 따로 떠 있던 상태배지("사용" 등)를 제목 앞으로 이동. */}
            <h3 style={{ fontSize: 17, fontWeight: 600, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
              <StatusBadge status={a.status} />{a.asset_name}
            </h3>
            {/* 2026-09(2차) 요청: 제목 바로 아래에 있던 관리번호 표시를 삭제(관리번호는 아래
                상세정보 목록에 별도 항목으로 이미 표시됨). */}
          </div>
          {/* 2026-09(3차) 요청: 닫기버튼 왼쪽에 사진/라이선스/인쇄 버튼 추가. 사진·라이선스는
              해당 데이터가 없으면 아예 노출하지 않는다(요청사항). */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {a.image_path && (
              <button onClick={() => setPreviewModal({ type: 'photo' })}
                style={{ background: '#f0f6ff', color: '#185FA5', border: '1px solid #185FA5', borderRadius: 6, padding: '5px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>🖼️ 사진</button>
            )}
            {a.license_attach_count > 0 && (
              <button onClick={openLicensePreview}
                style={{ background: '#f0f6ff', color: '#185FA5', border: '1px solid #185FA5', borderRadius: 6, padding: '5px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>📄 라이선스</button>
            )}
            <button onClick={handlePrintAll} disabled={printing}
              style={{ background: printing ? '#aaa' : '#0F6E56', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 10px', fontSize: 12, fontWeight: 600, cursor: printing ? 'default' : 'pointer', whiteSpace: 'nowrap' }}>🖨️ 인쇄</button>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#999' }}>✕</button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 4, margin: '14px 0', borderBottom: `1px solid ${C.border}` }}>
          {[['info', '상세정보'], ['history', `변경이력(${card.history.length})`], ['repairs', `수리내역(${card.repairs.length})`], ['loans', `대여내역(${card.loans.length})`], ['memos', `메모(${card.memos.length})`]].map(([k, l]) => (
            <button key={k} onClick={() => setSub(k)} style={{ padding: '8px 12px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
              color: sub === k ? '#185FA5' : '#888', borderBottom: sub === k ? '2px solid #185FA5' : '2px solid transparent' }}>{l}</button>
          ))}
        </div>

        {sub === 'info' && (
          <div>
            {/* 2026-08-31 — 목록 개편에 맞춰 구분/관리번호/라이선스/갱신·만료일 표시 추가. */}
            {[['구분', a.division_label], ['관리번호', a.asset_code], ['모델명', a.model_name], ['상세규격', a.spec], ['수량', a.qty], ['라이선스', a.license],
              ['시리얼번호', a.serial_no], ['RFID', a.rfid_code],
              ['사용부서', a.department], ['담당자', a.manager_name], ['담당자 연락처', a.manager_contact],
              ['사용자', a.user_name], ['구매일', a.acquire_date], ['구매금액(VAT포함)', a.acquire_price ? `${moneyFmt(a.acquire_price)}원` : null],
              /* 2026-09(6차) 요청: 영구 체크시 갱신/만료일 칸에 '영구' 표시 */
              /* 2026-09-13(6차) 요청: 잔존가액 옆 "(감가상각 : 정액법 · xx%/년)" 표기 복원 +
                 "감가상각계정" 항목 자체는 상세화면에서 삭제(아래에서 제거). */
              ['잔존가액', (a.auto_residual_value ?? a.residual_value)
                ? `${moneyFmt(a.auto_residual_value ?? a.residual_value)}원${a.residual_auto ? ' (자동계산)' : ''}${assetDepLabel(a) ? ` (감가상각 : ${assetDepLabel(a).replace(', ', ' · ')})` : ''}`
                : null],
              ['갱신/만료일', a.is_permanent ? '영구' : a.renew_expire_date], ['구매처', a.vendor_name],
              ['AS담당자', a.as_manager_name], ['AS담당자 연락처', a.as_manager_contact],
              ['상각제외', a.depreciation_exempt ? '예' : null],
              ['비고', a.note], ['등록자', a.created_by], ['등록일', a.created_at?.slice(0, 10)],
            ].map(([label, val]) => val ? (
              <div key={label} style={{ display: 'flex', gap: 12, marginBottom: 9, fontSize: 14 }}>
                <span style={{ width: 130, color: '#888', flexShrink: 0 }}>{label}</span><span style={{ color: '#1a1a1a' }}>{val}</span>
              </div>
            ) : null)}

            {/* 2026-09-13(7차) 요청: "제일 아래쪽에 있는 라이선스 첨부파일 및 사진 첨부파일을 보여주는
                부분은 삭제" — 상세화면 정보탭 하단의 상세규격/라이선스 첨부파일 인라인 표시 블록 제거.
                헤더의 🖼️ 사진 / 라이선스 버튼(미리보기 모달)은 그대로 유지. */}

            <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
              <button onClick={() => printPdfFromApi(`/assets/label/pdf?ids=${a.id}`)} style={{ ...btn('#0F6E56'), flex: 1 }}>🏷️ 라벨출력</button>
              {canWrite && <button onClick={() => onEdit(a)} style={{ ...btn('#f0f6ff', '#185FA5'), flex: 1, border: '1px solid #185FA5' }}>✏️ 수정</button>}
              {canDelete && <button onClick={() => onDelete(a.id)} style={{ ...btn('#fee2e2', '#dc2626'), flex: 1, border: '1px solid #dc2626' }}>🗑 삭제</button>}
            </div>
          </div>
        )}

        {sub === 'history' && (
          <SimpleTable rows={card.history} cols={[['change_type', '항목'], ['before_value', '변경 전'], ['after_value', '변경 후'], ['changed_by', '변경자'], ['changed_at', '일시']]} empty="변경이력이 없습니다." />
        )}

        {sub === 'repairs' && <RepairPanel assetId={a.id} rows={card.repairs} vendors={vendors} canWrite={canWrite} onChanged={() => { load(); onChanged(); }} />}
        {sub === 'loans' && <LoanPanel assetId={a.id} rows={card.loans} vendors={vendors} canWrite={canWrite} onChanged={() => { load(); onChanged(); }} />}
        {sub === 'memos' && <MemoPanel assetId={a.id} rows={card.memos} canWrite={canWrite} onChanged={load} />}
      </div>
    </div>
    {previewModal?.type === 'photo' && (
      <AssetFilePreviewModal title="사진 미리보기" url={`/uploads/${a.image_path}`} onClose={() => setPreviewModal(null)} />
    )}
    {previewModal?.type === 'license' && (
      <AssetFilePreviewModal title="라이선스 첨부 미리보기" files={previewModal.files} onClose={() => setPreviewModal(null)} />
    )}
    </>
  );
}

function SimpleTable({ rows, cols, empty }) {
  if (!rows || rows.length === 0) return <div style={{ padding: '30px 0', textAlign: 'center', color: '#aaa', fontSize: 13 }}>{empty}</div>;
  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols.length}, 1fr)`, ...th, padding: '8px' }}>
        {cols.map(([k, l]) => <span key={k}>{l}</span>)}
      </div>
      {rows.map((r, i) => (
        <div key={r.id || i} style={{ display: 'grid', gridTemplateColumns: `repeat(${cols.length}, 1fr)`, padding: '7px 8px', fontSize: 12.5, textAlign: 'center', borderTop: i > 0 ? '1px solid #f0f0f0' : 'none' }}>
          {cols.map(([k]) => <span key={k} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r[k] ?? '-'}</span>)}
        </div>
      ))}
    </div>
  );
}

function RepairPanel({ assetId, rows, vendors, canWrite, onChanged }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ request_date: getKstDateString(), request_content: '', vendor_id: '' });
  const [completing, setCompleting] = useState(null);
  const [compForm, setCompForm] = useState({ repair_date: getKstDateString(), repair_cost: '', repair_content: '' });

  const submitRequest = async () => {
    if (!form.request_content.trim()) return alert('수리요청 내용을 입력해주세요.');
    await API.post(`/assets/${assetId}/repairs`, form); setShowForm(false); setForm({ request_date: getKstDateString(), request_content: '', vendor_id: '' }); onChanged();
  };
  const submitComplete = async (id) => {
    await API.patch(`/assets/repairs/${id}/complete`, { ...compForm, repair_cost: onlyDigits(compForm.repair_cost) });
    setCompleting(null); onChanged();
  };

  return (
    <div>
      {canWrite && (
        <div style={{ marginBottom: 12 }}>
          {!showForm ? <button onClick={() => setShowForm(true)} style={smallBtn('#185FA5')}>+ 수리요청</button> : (
            <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: 12, background: '#f8fafc' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 8, marginBottom: 8 }}>
                <input type="date" value={form.request_date} onChange={e => setForm({ ...form, request_date: e.target.value })} style={{ ...input, padding: '6px 8px' }} />
                <select value={form.vendor_id} onChange={e => setForm({ ...form, vendor_id: e.target.value })} style={{ ...input, padding: '6px 8px' }}>
                  <option value="">수리업체 선택안함</option>{vendors.map(v => <option key={v.id} value={v.id}>{v.vendor_name}</option>)}
                </select>
              </div>
              <textarea value={form.request_content} onChange={e => setForm({ ...form, request_content: e.target.value })} rows={2} placeholder="수리요청 내용" style={{ ...input, width: '100%', boxSizing: 'border-box', marginBottom: 8, resize: 'vertical' }} />
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={submitRequest} style={smallBtn('#185FA5')}>등록</button>
                <button onClick={() => setShowForm(false)} style={smallBtn('#e5e7eb', '#555')}>취소</button>
              </div>
            </div>
          )}
        </div>
      )}
      {(!rows || rows.length === 0) ? <div style={{ padding: '20px 0', textAlign: 'center', color: '#aaa', fontSize: 13 }}>수리내역이 없습니다.</div> : rows.map(r => (
        <div key={r.id} style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, marginBottom: 8, fontSize: 13 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span><StatusBadge status={r.status} /> {r.request_date} {r.vendor_name ? `· ${r.vendor_name}` : ''}</span>
            {canWrite && r.status === '요청' && <button onClick={() => setCompleting(r.id)} style={{ background: 'none', border: 'none', color: '#185FA5', cursor: 'pointer', fontSize: 12 }}>완료처리</button>}
          </div>
          <div style={{ color: '#555', marginTop: 4 }}>{r.request_content}</div>
          {r.status === '완료' && <div style={{ color: '#888', marginTop: 4, fontSize: 12 }}>완료: {r.repair_date} / 비용 {moneyFmt(r.repair_cost)}원 / {r.repair_content}</div>}
          {completing === r.id && (
            <div style={{ marginTop: 8, padding: 8, background: '#f8fafc', borderRadius: 6 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: 6, marginBottom: 6 }}>
                <input type="date" value={compForm.repair_date} onChange={e => setCompForm({ ...compForm, repair_date: e.target.value })} style={{ ...input, padding: '5px 8px' }} />
                <input value={compForm.repair_cost} onChange={e => setCompForm({ ...compForm, repair_cost: formatComma(e.target.value) })} placeholder="수리비용" style={{ ...input, padding: '5px 8px' }} />
              </div>
              <textarea value={compForm.repair_content} onChange={e => setCompForm({ ...compForm, repair_content: e.target.value })} rows={2} placeholder="수리 내용" style={{ ...input, width: '100%', boxSizing: 'border-box', marginBottom: 6, resize: 'vertical' }} />
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => submitComplete(r.id)} style={smallBtn('#0F6E56')}>완료 저장</button>
                <button onClick={() => setCompleting(null)} style={smallBtn('#e5e7eb', '#555')}>취소</button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function LoanPanel({ assetId, rows, vendors, canWrite, onChanged }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ loan_date: getKstDateString(), borrower: '', vendor_id: '', loan_content: '' });

  const submitLoan = async () => {
    if (!form.borrower.trim()) return alert('대여자를 입력해주세요.');
    await API.post(`/assets/${assetId}/loans`, form); setShowForm(false); setForm({ loan_date: getKstDateString(), borrower: '', vendor_id: '', loan_content: '' }); onChanged();
  };
  const doReturn = async (id) => { await API.patch(`/assets/loans/${id}/return`, { return_date: getKstDateString() }); onChanged(); };

  return (
    <div>
      {canWrite && (
        <div style={{ marginBottom: 12 }}>
          {!showForm ? <button onClick={() => setShowForm(true)} style={smallBtn('#185FA5')}>+ 대여등록</button> : (
            <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: 12, background: '#f8fafc' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 8, marginBottom: 8 }}>
                <input type="date" value={form.loan_date} onChange={e => setForm({ ...form, loan_date: e.target.value })} style={{ ...input, padding: '6px 8px' }} />
                <input value={form.borrower} onChange={e => setForm({ ...form, borrower: e.target.value })} placeholder="대여자명" style={{ ...input, padding: '6px 8px' }} />
              </div>
              <select value={form.vendor_id} onChange={e => setForm({ ...form, vendor_id: e.target.value })} style={{ ...input, width: '100%', boxSizing: 'border-box', marginBottom: 8 }}>
                <option value="">대여처(외부업체) 선택안함</option>{vendors.map(v => <option key={v.id} value={v.id}>{v.vendor_name}</option>)}
              </select>
              <textarea value={form.loan_content} onChange={e => setForm({ ...form, loan_content: e.target.value })} rows={2} placeholder="대여 목적/내용" style={{ ...input, width: '100%', boxSizing: 'border-box', marginBottom: 8, resize: 'vertical' }} />
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={submitLoan} style={smallBtn('#185FA5')}>등록</button>
                <button onClick={() => setShowForm(false)} style={smallBtn('#e5e7eb', '#555')}>취소</button>
              </div>
            </div>
          )}
        </div>
      )}
      {(!rows || rows.length === 0) ? <div style={{ padding: '20px 0', textAlign: 'center', color: '#aaa', fontSize: 13 }}>대여내역이 없습니다.</div> : rows.map(r => (
        <div key={r.id} style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, marginBottom: 8, fontSize: 13 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span><StatusBadge status={r.status === '대여중' ? '대여' : '사용'} /> {r.loan_date} · {r.borrower} {r.vendor_name ? `(${r.vendor_name})` : ''}</span>
            {canWrite && r.status === '대여중' && <button onClick={() => doReturn(r.id)} style={{ background: 'none', border: 'none', color: '#185FA5', cursor: 'pointer', fontSize: 12 }}>반납처리</button>}
          </div>
          <div style={{ color: '#555', marginTop: 4 }}>{r.loan_content}</div>
          {r.status === '반납완료' && <div style={{ color: '#888', marginTop: 4, fontSize: 12 }}>반납일: {r.return_date}</div>}
        </div>
      ))}
    </div>
  );
}

function MemoPanel({ assetId, rows, canWrite, onChanged }) {
  const [content, setContent] = useState('');
  const submit = async () => { if (!content.trim()) return; await API.post(`/assets/${assetId}/memos`, { content: content.trim() }); setContent(''); onChanged(); };
  const remove = async (id) => { if (!window.confirm('삭제하시겠습니까?')) return; await API.delete(`/assets/memos/${id}`); onChanged(); };

  return (
    <div>
      {canWrite && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <input value={content} onChange={e => setContent(e.target.value)} onKeyDown={e => e.key === 'Enter' && submit()}
            placeholder="메모 입력" style={{ ...input, flex: 1 }} />
          <button onClick={submit} style={smallBtn('#185FA5')}>등록</button>
        </div>
      )}
      {(!rows || rows.length === 0) ? <div style={{ padding: '20px 0', textAlign: 'center', color: '#aaa', fontSize: 13 }}>메모가 없습니다.</div> : rows.map(m => (
        <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, padding: '8px 0', borderTop: '1px solid #f0f0f0', fontSize: 13 }}>
          <div>
            <div>{m.content}</div>
            <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>{m.created_dept} {m.created_by} · {m.memo_date?.slice(0, 16)}</div>
          </div>
          {canWrite && <button onClick={() => remove(m.id)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 12, flexShrink: 0 }}>삭제</button>}
        </div>
      ))}
    </div>
  );
}

// 2026-08-31(3차) — ReportTab(현황조회/보고서)은 BUILTIN_TAB_COMPONENTS에 연결된 적이 없는
// dead code였다(어느 탭에서도 렌더되지 않음). 기준정보/재물조사 정리와 함께 제거한다.

// ══════════════════════════════════════════════════════════════
// 4) 감가상각
// ══════════════════════════════════════════════════════════════
function DepreciationTab({ settings, loadSettings, canDepRead, canDepWrite, registerPrintExport }) {
  const [sub, setSub] = useState('accounts');
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        {[['accounts', '📚 계정과목관리'], ['ledger', '📄 감가상각명세서'], ['summary', '📊 감가상각총괄표'], ['settings', '⚙️ 환경설정']].map(([k, l]) => (
          <button key={k} onClick={() => setSub(k)} style={{ ...smallBtn(sub === k ? '#185FA5' : '#f1f5f9', sub === k ? '#fff' : '#555'), padding: '8px 16px' }}>{l}</button>
        ))}
      </div>
      {sub === 'accounts' && <DepAccountsPanel canWrite={canDepWrite} registerPrintExport={registerPrintExport} />}
      {sub === 'ledger' && <DepLedgerPanel canRead={canDepRead} registerPrintExport={registerPrintExport} />}
      {sub === 'summary' && <DepSummaryPanel canRead={canDepRead} registerPrintExport={registerPrintExport} />}
      {sub === 'settings' && <DepSettingsPanel settings={settings} loadSettings={loadSettings} canWrite={canDepWrite} registerPrintExport={registerPrintExport} />}
    </div>
  );
}

function DepAccountsPanel({ canWrite, registerPrintExport }) {
  const [accounts, setAccounts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const load = () => API.get('/assets/depreciation/accounts').then(r => setAccounts(r.data)).catch(() => {});
  // 2026-08-31(4차) — 아래 useEffect(() => { load(); }, ...) 참고: load()가 프라미스를
  // 그대로 반환해서 생기는 언마운트시 크래시를 막기 위해 블록 바디로 감쌌다.
  useEffect(() => { load(); }, []);
  const remove = async (id) => { if (!window.confirm('삭제하시겠습니까?')) return; try { await API.delete(`/assets/depreciation/accounts/${id}`); load(); } catch (e) { alert(e.response?.data?.message || '오류'); } };

  useEffect(() => {
    registerPrintExport({
      onPrint: () => printListWindow({
        menu: '자산관리', title: '📚 감가상각 — 계정과목관리',
        columns: [
          { label: '계정코드', value: a => a.account_code }, { label: '계정명', value: a => a.account_name }, { label: '방법', value: a => a.method },
          { label: '내용연수', value: a => `${a.useful_life}년` }, { label: '상각율', value: a => a.dep_rate ? `${a.dep_rate}%` : '자동' }, { label: '단수처리', value: a => a.decimal_mode },
        ], rows: accounts, listName: `계정과목(${accounts.length}건)`,
      }),
      onExport: () => exportListXlsx({
        menu: '자산관리', filename: `IMS_감가상각_계정과목_${new Date().toISOString().slice(0, 10)}.xlsx`, sheetName: '계정과목',
        columns: [
          { label: '계정코드', value: a => a.account_code, wch: 12 }, { label: '계정명', value: a => a.account_name, wch: 18 }, { label: '방법', value: a => a.method, wch: 10 },
          { label: '내용연수', value: a => a.useful_life, wch: 10 }, { label: '상각율', value: a => a.dep_rate || '', wch: 10 }, { label: '단수처리', value: a => a.decimal_mode, wch: 10 },
        ], rows: accounts,
      }),
    });
    return () => registerPrintExport(null);
  }, [accounts]); // eslint-disable-line

  return (
    <Section title="계정과목관리" action={canWrite && <button onClick={() => { setEditData(null); setShowForm(true); }} style={btn('#185FA5')}>+ 계정과목 등록</button>}>
      {accounts.length === 0 ? <div style={{ padding: '30px 0', textAlign: 'center', color: '#aaa', fontSize: 13 }}>등록된 계정과목이 없습니다.</div> : (
        <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr 100px 90px 90px 90px 70px', ...th, padding: '9px' }}>
            <span>계정코드</span><span>계정명</span><span>방법</span><span>내용연수</span><span>상각율</span><span>단수처리</span><span>관리</span>
          </div>
          {accounts.map((a, i) => (
            <div key={a.id} style={{ display: 'grid', gridTemplateColumns: '100px 1fr 100px 90px 90px 90px 70px', ...td, borderTop: i > 0 ? undefined : 'none' }}>
              <span>{a.account_code || '-'}</span><span style={{ fontWeight: 600 }}>{a.account_name}</span><span>{a.method}</span>
              <span>{a.useful_life}년</span><span>{a.dep_rate ? `${a.dep_rate}%` : '자동'}</span><span>{a.decimal_mode}</span>
              <span>{canWrite && <>
                <button onClick={() => { setEditData(a); setShowForm(true); }} style={{ background: 'none', border: 'none', color: '#185FA5', fontSize: 12, cursor: 'pointer', marginRight: 6 }}>수정</button>
                <button onClick={() => remove(a.id)} style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: 12, cursor: 'pointer' }}>삭제</button>
              </>}</span>
            </div>
          ))}
        </div>
      )}
      {showForm && <DepAccountForm editData={editData} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </Section>
  );
}

function DepAccountForm({ editData, onClose, onSaved }) {
  const [form, setForm] = useState({
    account_code: editData?.account_code || '', account_name: editData?.account_name || '',
    method: editData?.method || '정액법', useful_life: editData?.useful_life || 5,
    dep_rate: editData?.dep_rate || '', decimal_mode: editData?.decimal_mode || '반올림',
  });
  const submit = async () => {
    if (!form.account_name.trim()) return alert('계정명을 입력해주세요.');
    try {
      if (editData) await API.put(`/assets/depreciation/accounts/${editData.id}`, form);
      else await API.post('/assets/depreciation/accounts', form);
      onSaved();
    } catch (e) { alert(e.response?.data?.message || '오류가 발생했습니다.'); }
  };
  return (
    <div style={overlayStyle}>
      <div style={{ ...modalStyle, width: 420 }} onClick={e => e.stopPropagation()}>
        <ModalHeader title={editData ? '계정과목 수정' : '계정과목 등록'} onClose={onClose} />
        <label style={labelStyle}>계정코드</label><input value={form.account_code} onChange={e => setForm({ ...form, account_code: e.target.value })} style={formInput} />
        <label style={labelStyle}>계정명 *</label><input value={form.account_name} onChange={e => setForm({ ...form, account_name: e.target.value })} style={formInput} />
        <label style={labelStyle}>상각방법</label>
        <select value={form.method} onChange={e => setForm({ ...form, method: e.target.value })} style={formInput}>
          <option value="정액법">정액법</option><option value="정률법">정률법</option>
        </select>
        <label style={labelStyle}>내용연수(년)</label><input type="number" value={form.useful_life} onChange={e => setForm({ ...form, useful_life: e.target.value })} style={formInput} />
        <label style={labelStyle}>상각율(%) — 정률법에서만 사용, 비워두면 자동계산</label>
        <input value={form.dep_rate} onChange={e => setForm({ ...form, dep_rate: e.target.value })} style={formInput} />
        <label style={labelStyle}>단수처리</label>
        <select value={form.decimal_mode} onChange={e => setForm({ ...form, decimal_mode: e.target.value })} style={formInput}>
          <option value="반올림">반올림</option><option value="절사">절사</option>
        </select>
        <button onClick={submit} style={{ ...btn('#185FA5'), width: '100%', padding: 12, marginTop: 8 }}>{editData ? '수정 완료' : '등록'}</button>
      </div>
    </div>
  );
}

function DepLedgerPanel({ canRead, registerPrintExport }) {
  const [ym, setYm] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState({ rows: [] });
  const load = () => API.get('/assets/depreciation/ledger', { params: { year_month: ym } }).then(r => setData(r.data)).catch(() => {});
  useEffect(() => { if (canRead !== false) load(); }, [ym]); // eslint-disable-line
  const totalPeriod = data.rows.reduce((s, r) => s + (r.periodDep || 0), 0);

  useEffect(() => {
    registerPrintExport({
      onPrint: () => printListWindow({
        menu: '자산관리', title: `📄 감가상각명세서 (${ym})`,
        columns: [
          { label: '자산코드', value: r => r.asset_code }, { label: '자산명', value: r => r.asset_name }, { label: '부서', value: r => r.department },
          { label: '계정과목', value: r => r.account_name }, { label: '취득가액', value: r => moneyFmt(r.acquire_price) },
          { label: '전기상각누계', value: r => moneyFmt(Math.round(r.prevAccumDep)) }, { label: '당기상각', value: r => moneyFmt(Math.round(r.periodDep)) },
          { label: '장부가액', value: r => moneyFmt(Math.round(r.bookValue)) },
        ], rows: data.rows, listName: `감가상각명세서(${ym})`,
      }),
      onExport: () => exportListXlsx({
        menu: '자산관리', filename: `IMS_감가상각명세서_${ym}.xlsx`, sheetName: '감가상각명세서',
        columns: [
          { label: '자산코드', value: r => r.asset_code, wch: 12 }, { label: '자산명', value: r => r.asset_name, wch: 20 }, { label: '부서', value: r => r.department || '', wch: 10 },
          { label: '계정과목', value: r => r.account_name, wch: 14 }, { label: '취득가액', value: r => r.acquire_price || 0, wch: 12 },
          { label: '전기상각누계', value: r => Math.round(r.prevAccumDep) || 0, wch: 14 }, { label: '당기상각', value: r => Math.round(r.periodDep) || 0, wch: 12 },
          { label: '장부가액', value: r => Math.round(r.bookValue) || 0, wch: 12 },
        ], rows: data.rows,
      }),
    });
    return () => registerPrintExport(null);
  }, [data, ym]); // eslint-disable-line

  return (
    <Section title="감가상각명세서" action={<input type="month" value={ym} onChange={e => setYm(e.target.value)} style={{ ...input, padding: '6px 10px' }} />}>
      {data.rows.length === 0 ? <div style={{ padding: '30px 0', textAlign: 'center', color: '#aaa', fontSize: 13 }}>대상 자산이 없습니다.</div> : (
        <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, overflowX: 'auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr 90px 100px 110px 110px 110px 110px', ...th, padding: '9px' }}>
            <span>자산코드</span><span>자산명</span><span>부서</span><span>계정과목</span><span>취득가액</span><span>전기상각누계</span><span>당기상각</span><span>장부가액</span>
          </div>
          {data.rows.map(r => (
            <div key={r.asset_id} style={{ display: 'grid', gridTemplateColumns: '100px 1fr 90px 100px 110px 110px 110px 110px', ...td }}>
              <span>{r.asset_code}</span><span style={{ textAlign: 'left', fontWeight: 600 }}>{r.asset_name}</span><span>{r.department || '-'}</span>
              <span>{r.account_name}</span><span>{moneyFmt(r.acquire_price)}</span><span>{moneyFmt(Math.round(r.prevAccumDep))}</span>
              <span>{moneyFmt(Math.round(r.periodDep))}</span><span>{moneyFmt(Math.round(r.bookValue))}</span>
            </div>
          ))}
        </div>
      )}
      <div style={{ marginTop: 10, fontSize: 13, color: '#555', fontWeight: 600 }}>당기 상각비 합계: {moneyFmt(Math.round(totalPeriod))}원</div>
    </Section>
  );
}

function DepSummaryPanel({ canRead, registerPrintExport }) {
  const [ym, setYm] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState({ rows: [] });
  useEffect(() => { if (canRead !== false) API.get('/assets/depreciation/summary', { params: { year_month: ym } }).then(r => setData(r.data)).catch(() => {}); }, [ym]); // eslint-disable-line

  useEffect(() => {
    registerPrintExport({
      onPrint: () => printListWindow({
        menu: '자산관리', title: `📊 감가상각총괄표 (${ym})`,
        columns: [
          { label: '계정과목', value: r => r.account }, { label: '자산건수', value: r => r.count }, { label: '취득가액 합계', value: r => moneyFmt(r.acquirePrice) },
          { label: '상각누계액 합계', value: r => moneyFmt(Math.round(r.accumDep)) }, { label: '장부가액 합계', value: r => moneyFmt(Math.round(r.bookValue)) },
        ], rows: data.rows, listName: `감가상각총괄표(${ym})`,
      }),
      onExport: () => exportListXlsx({
        menu: '자산관리', filename: `IMS_감가상각총괄표_${ym}.xlsx`, sheetName: '감가상각총괄표',
        columns: [
          { label: '계정과목', value: r => r.account, wch: 16 }, { label: '자산건수', value: r => r.count, wch: 10 }, { label: '취득가액 합계', value: r => r.acquirePrice || 0, wch: 14 },
          { label: '상각누계액 합계', value: r => Math.round(r.accumDep) || 0, wch: 16 }, { label: '장부가액 합계', value: r => Math.round(r.bookValue) || 0, wch: 14 },
        ], rows: data.rows,
      }),
    });
    return () => registerPrintExport(null);
  }, [data, ym]); // eslint-disable-line

  return (
    <Section title="감가상각총괄표" action={<input type="month" value={ym} onChange={e => setYm(e.target.value)} style={{ ...input, padding: '6px 10px' }} />}>
      {data.rows.length === 0 ? <div style={{ padding: '30px 0', textAlign: 'center', color: '#aaa', fontSize: 13 }}>데이터가 없습니다.</div> : (
        <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px 140px 140px 140px', ...th, padding: '9px' }}>
            <span>계정과목</span><span>자산건수</span><span>취득가액 합계</span><span>상각누계액 합계</span><span>장부가액 합계</span>
          </div>
          {data.rows.map((r, i) => (
            <div key={r.account} style={{ display: 'grid', gridTemplateColumns: '1fr 90px 140px 140px 140px', ...td, borderTop: i > 0 ? undefined : 'none' }}>
              <span style={{ textAlign: 'left', fontWeight: 600 }}>{r.account}</span><span>{r.count}</span>
              <span>{moneyFmt(r.acquirePrice)}</span><span>{moneyFmt(Math.round(r.accumDep))}</span><span>{moneyFmt(Math.round(r.bookValue))}</span>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

function DepSettingsPanel({ settings, loadSettings, canWrite, registerPrintExport }) {
  const [form, setForm] = useState(null);
  useEffect(() => { if (settings) setForm({ company_code_prefix: settings.company_code_prefix || 'NI', barcode_prefix: settings.barcode_prefix || 'AST', dep_start_month: settings.dep_start_month || '', dep_residual_default: settings.dep_residual_default || 0, status_options: (settings.status_options_parsed || []).join(', '), extra_labels: (settings.extra_labels_parsed || []).join(', ') }); }, [settings]);

  // 환경설정 화면은 목록형 데이터가 없으므로 인쇄/내보내기 버튼은 비활성화 상태로 둔다.
  useEffect(() => { registerPrintExport({}); return () => registerPrintExport(null); }, []); // eslint-disable-line

  if (!form) return null;

  const submit = async () => {
    try {
      await API.put('/assets/settings/current', {
        company_code_prefix: (form.company_code_prefix || 'NI').trim().toUpperCase(),
        barcode_prefix: form.barcode_prefix, dep_start_month: form.dep_start_month, dep_residual_default: Number(onlyDigits(String(form.dep_residual_default))) || 0,
        status_options: form.status_options.split(',').map(s => s.trim()).filter(Boolean),
        extra_labels: form.extra_labels.split(',').map(s => s.trim()).filter(Boolean),
      });
      loadSettings(); alert('저장되었습니다.');
    } catch (e) { alert(e.response?.data?.message || '오류가 발생했습니다.'); }
  };

  return (
    <Section title="자산관리 환경설정">
      <label style={labelStyle}>회사 공통접두어(관리번호용, 예: NI)</label>
      <input value={form.company_code_prefix} onChange={e => setForm({ ...form, company_code_prefix: e.target.value.toUpperCase() })} style={formInput} disabled={!canWrite} placeholder="예: NI" />
      <label style={labelStyle}>바코드/QR 접두사</label>
      <input value={form.barcode_prefix} onChange={e => setForm({ ...form, barcode_prefix: e.target.value })} style={formInput} disabled={!canWrite} />
      <label style={labelStyle}>감가상각 시작월(선택)</label>
      <input type="month" value={form.dep_start_month} onChange={e => setForm({ ...form, dep_start_month: e.target.value })} style={formInput} disabled={!canWrite} />
      <label style={labelStyle}>잔존가액 기본값</label>
      <input value={formatComma(form.dep_residual_default)} onChange={e => setForm({ ...form, dep_residual_default: e.target.value })} style={formInput} disabled={!canWrite} />
      <label style={labelStyle}>자산 상태 옵션(콤마로 구분)</label>
      <input value={form.status_options} onChange={e => setForm({ ...form, status_options: e.target.value })} style={formInput} disabled={!canWrite} />
      <label style={labelStyle}>추가항목 라벨(콤마로 구분, 최대 8개)</label>
      <input value={form.extra_labels} onChange={e => setForm({ ...form, extra_labels: e.target.value })} style={formInput} disabled={!canWrite} />
      {canWrite && <button onClick={submit} style={{ ...btn('#185FA5'), width: '100%', padding: 12, marginTop: 8 }}>저장</button>}
    </Section>
  );
}

// 2026-08-31(3차) — AuditTab/AuditPlanForm(재물조사)과 DisposedAssetsModal(폐기항목)은 상단
// ══════════════════════════════════════════════════════════════
// 6) 관리항목(상단 탭 관리) 모달 — 완전히 새로운 탭 추가 + 기존 탭 라벨/순서/노출여부 관리
// ══════════════════════════════════════════════════════════════
function TabManageModal({ tabs, error, canManage, onClose, onChanged }) {
  const [newLabel, setNewLabel] = useState('');
  const [newPrefix, setNewPrefix] = useState('');
  // 2026-09(12차) 요청 — 새 탭을 "자산목록형"(기본, 비품/소프트웨어와 동일한 자산목록+등록폼)
  // 또는 "고장수리형"(화장실/수도설비/전기/자동문/유리문 등 기타 시설 고장수리 접수·처리 이력을
  // 자산 등록 여부와 무관하게 남기는 전용 화면)으로 고를 수 있다.
  const [newType, setNewType] = useState('asset');
  // 2026-09-13(2차) 추가 — 관리항목 설명란(감가설정 화면의 "자산기준"에도 그대로 표기됨).
  const [newDesc, setNewDesc] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState('');
  const [editPrefix, setEditPrefix] = useState('');
  const [editType, setEditType] = useState('asset');
  const [editDesc, setEditDesc] = useState('');
  const [msg, setMsg] = useState('');
  const sorted = [...tabs].sort((a, b) => a.sort_order - b.sort_order);

  const notify = (e) => { setMsg(e?.response?.data?.message || '오류가 발생했습니다.'); setTimeout(() => setMsg(''), 3000); };

  // 2026-08-31 추가 — 탭별 "관리번호 이니셜"(code_prefix). 회사 공통접두어(환경설정에서 지정)와
  // 조합되어 NI-SW-0001 같은 관리번호를 자동채번한다(사용자 요청사항 그대로 반영).
  const addTab = async () => {
    if (!newLabel.trim()) return;
    try {
      await API.post('/assets/menu-tabs', { label: newLabel.trim(), code_prefix: newPrefix.trim(), tab_type: newType, description: newDesc.trim() });
      setNewLabel(''); setNewPrefix(''); setNewType('asset'); setNewDesc(''); onChanged();
    } catch (e) { notify(e); }
  };
  const saveLabel = async (id) => {
    try { await API.put(`/assets/menu-tabs/${id}`, { label: editLabel, code_prefix: editPrefix, tab_type: editType, description: editDesc }); setEditingId(null); onChanged(); } catch (e) { notify(e); }
  };
  const move = async (idx, dir) => {
    const arr = [...sorted];
    const target = idx + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    try { await API.put('/assets/menu-tabs/reorder', { order: arr.map(t => t.id) }); onChanged(); } catch (e) { notify(e); }
  };
  const remove = async (t) => {
    // 2026-08-31 — 기본 제공 탭도 삭제 가능하도록 바뀌면서, 그 탭을 삭제하면 등록해둔 자산은
    // 그대로 남지만 그 자산유형 화면(탭)에는 다시 접근할 수 없다는 점을 확인창에서 명확히 알린다.
    const msg = t.is_builtin
      ? `"${t.label}"은(는) 기본 제공 탭입니다. 삭제하면 이 탭 화면은 다시 만들 수 없고, 이미 등록된 자산 데이터는 지워지지 않지만 탭이 없어 목록을 볼 수 없게 됩니다. 그래도 삭제하시겠습니까?`
      : `"${t.label}" 탭을 삭제하시겠습니까? 등록된 내용도 함께 삭제됩니다.`;
    if (!window.confirm(msg)) return;
    try { await API.delete(`/assets/menu-tabs/${t.id}`); onChanged(); } catch (e) { notify(e); }
  };

  return (
    <div style={overlayStyle}>
      <div style={{ ...modalStyle, width: 760 }} onClick={e => e.stopPropagation()}>
        <ModalHeader title="📑 관리항목 — 상단 탭 관리" onClose={onClose} />
        <div style={{ fontSize: 12, color: '#888', marginBottom: 14 }}>
          모든 탭의 이름(라벨)·순서·삭제 여부를 자유롭게 관리할 수 있습니다. 처음부터 있던 탭을
          삭제하면 그 화면(탭)은 다시 만들 수 없으니 신중히 진행해주세요(등록된 자산 데이터
          자체는 지워지지 않습니다). 새로 추가하는 탭은 "자산목록형"(비품/소프트웨어와 동일한
          자산목록+등록폼) 또는 "고장수리형"(화장실/수도설비/전기/자동문/유리문 등 기타 시설
          고장수리 접수·처리 내역을 자산 등록 여부와 무관하게 남기는 화면) 중 하나로 만들 수
          있습니다.
        </div>
        {msg && <div style={{ fontSize: 12, color: '#dc2626', marginBottom: 10 }}>{msg}</div>}
        {error && (
          <div style={{ fontSize: 12, color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 12px', marginBottom: 14, lineHeight: 1.5 }}>
            ⚠ {error}
            <div style={{ marginTop: 6 }}>
              <button onClick={onChanged} style={{ background: 'none', border: '1px solid #dc2626', color: '#dc2626', borderRadius: 6, padding: '3px 10px', fontSize: 12, cursor: 'pointer' }}>다시 시도</button>
            </div>
          </div>
        )}

        <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden', marginBottom: 14 }}>
          {sorted.length === 0 ? (
            <div style={{ padding: '20px 0', textAlign: 'center', color: '#aaa', fontSize: 13 }}>{error ? '탭 목록을 불러오지 못했습니다.' : '탭이 없습니다.'}</div>
          ) : sorted.map((t, i) => (
            <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderBottom: i < sorted.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <button onClick={() => move(i, -1)} disabled={!canManage || i === 0} style={{ background: 'none', border: 'none', cursor: canManage ? 'pointer' : 'default', color: '#888', fontSize: 11, lineHeight: 1 }}>▲</button>
                <button onClick={() => move(i, 1)} disabled={!canManage || i === sorted.length - 1} style={{ background: 'none', border: 'none', cursor: canManage ? 'pointer' : 'default', color: '#888', fontSize: 11, lineHeight: 1 }}>▼</button>
              </div>
              {editingId === t.id ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input autoFocus value={editLabel} onChange={e => setEditLabel(e.target.value)} onKeyDown={e => e.key === 'Enter' && saveLabel(t.id)}
                      style={{ flex: 1, padding: '5px 10px', border: '1px solid #185FA5', borderRadius: 6, fontSize: 15 }} />
                    <input value={editPrefix} onChange={e => setEditPrefix(e.target.value.toUpperCase())} onKeyDown={e => e.key === 'Enter' && saveLabel(t.id)}
                      placeholder="관리번호 이니셜(예: SW)" style={{ width: 130, padding: '5px 10px', border: '1px solid #185FA5', borderRadius: 6, fontSize: 13 }} />
                    {!t.is_builtin && (
                      <select value={editType} onChange={e => setEditType(e.target.value)}
                        style={{ width: 110, padding: '5px 6px', border: '1px solid #185FA5', borderRadius: 6, fontSize: 12.5 }}>
                        <option value="asset">자산목록형</option>
                        <option value="repair">고장수리형</option>
                      </select>
                    )}
                  </div>
                  {/* 2026-09-13(2차) 추가 — 관리항목 설명란(감가설정의 "자산기준"에도 표기됨). */}
                  <input value={editDesc} onChange={e => setEditDesc(e.target.value)} onKeyDown={e => e.key === 'Enter' && saveLabel(t.id)}
                    placeholder="설명(예: 기구 및 비품 — 감가설정 자산기준에 표기됩니다)"
                    style={{ padding: '5px 10px', border: '1px solid #185FA5', borderRadius: 6, fontSize: 13 }} />
                </div>
              ) : (
                <span style={{ flex: 1, fontSize: 15, fontWeight: 500, color: '#222', display: 'flex', alignItems: 'center', minWidth: 0 }}>
                  <span style={{ flexShrink: 0 }}>
                    {stripIcon(t.label)}
                    {t.code_prefix && <span style={{ marginLeft: 8, fontSize: 12, color: '#185FA5', fontWeight: 600 }}>[{t.code_prefix}]</span>}
                    {t.tab_type === 'repair' && <span style={{ marginLeft: 8, fontSize: 11.5, color: '#B45309', fontWeight: 600, background: '#FEF3C7', borderRadius: 5, padding: '2px 8px' }}>고장수리형</span>}
                  </span>
                  {t.description && (
                    <span title={t.description} style={{ marginLeft: 10, fontSize: 12.5, color: '#888', fontWeight: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                      {t.description}
                    </span>
                  )}
                </span>
              )}
              {/* 2026-08-31 — "기본" 배지를 지웠다(사용자 요청: "기본으로 잡혀있는 메뉴를 기본에서
                  해제"). 이름/순서 수정은 배지 유무와 무관하게 계속 가능했고, 이 배지가 "고정된
                  것처럼" 보여 혼란을 줬다. 삭제만 계속 막혀 있다는 사실은 위 안내문구로 대신 알린다.
                  2026-08-31(4차) — "커스텀" 배지도 사용자 요청으로 제거했다(새로 추가한 탭인지
                  여부를 굳이 구분해 보여줄 필요가 없다는 판단). */}
              {canManage && (
                <div style={{ display: 'flex', gap: 14, flexShrink: 0 }}>
                  {editingId === t.id ? (
                    <button onClick={() => saveLabel(t.id)} style={{ background: 'none', border: 'none', color: '#185FA5', fontSize: 13, fontWeight: 500, cursor: 'pointer', padding: 0 }}>저장</button>
                  ) : (
                    <button onClick={() => { setEditingId(t.id); setEditLabel(stripIcon(t.label)); setEditPrefix(t.code_prefix || ''); setEditType(t.tab_type === 'repair' ? 'repair' : 'asset'); setEditDesc(t.description || ''); }} style={{ background: 'none', border: 'none', color: '#555', fontSize: 13, fontWeight: 500, cursor: 'pointer', padding: 0 }}>수정</button>
                  )}
                  <button onClick={() => remove(t)} style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: 13, fontWeight: 500, cursor: 'pointer', padding: 0 }}>삭제</button>
                </div>
              )}
            </div>
          ))}
        </div>

        {canManage && (
          <div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input value={newLabel} onChange={e => setNewLabel(e.target.value)} onKeyDown={e => e.key === 'Enter' && addTab()}
                placeholder="새 탭 이름(예: 고장수리)" style={{ ...input, flex: 1 }} />
              <input value={newPrefix} onChange={e => setNewPrefix(e.target.value.toUpperCase())} onKeyDown={e => e.key === 'Enter' && addTab()}
                placeholder="관리번호 이니셜(예: SW)" style={{ ...input, width: 130 }} />
              <select value={newType} onChange={e => setNewType(e.target.value)} style={{ ...input, width: 110 }}>
                <option value="asset">자산목록형</option>
                <option value="repair">고장수리형</option>
              </select>
              <button onClick={addTab} disabled={!newLabel.trim()} style={btn(!newLabel.trim() ? '#aaa' : '#185FA5')}>+ 탭 추가</button>
            </div>
            {/* 2026-09-13(2차) 추가 — 관리항목 설명란. 감가설정 화면 "자산기준" 컬럼에 그대로 표기된다. */}
            <input value={newDesc} onChange={e => setNewDesc(e.target.value)} onKeyDown={e => e.key === 'Enter' && addTab()}
              placeholder="설명(예: 기구 및 비품 — 감가설정 자산기준에 표기됩니다)"
              style={{ ...input, width: '100%', boxSizing: 'border-box', marginTop: 8 }} />
          </div>
        )}
      </div>
    </div>
  );
}

// 관리항목에서 새로 추가한(기본 5개가 아닌) 탭의 실제 콘텐츠 — 제목+내용 간단 게시판
function TabNotesPanel({ tabId, label, canWrite, registerPrintExport }) {
  const [notes, setNotes] = useState([]);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', content: '' });
  const load = () => API.get('/assets/tab-notes', { params: { tab_id: tabId } }).then(r => setNotes(r.data)).catch(() => {});
  // 2026-08-31(4차) — load() 프라미스 반환 → 언마운트시 크래시 방지 (위 주석 참고)
  useEffect(() => { load(); }, [tabId]); // eslint-disable-line

  useEffect(() => {
    registerPrintExport({
      onPrint: () => printListWindow({
        menu: '자산관리', title: `📌 ${label}`,
        columns: [
          { label: '제목', value: n => n.title }, { label: '내용', value: n => n.content }, { label: '작성자', value: n => n.created_by }, { label: '작성일', value: n => n.created_at?.slice(0, 16) },
        ], rows: notes, listName: `${label}(${notes.length}건)`,
      }),
      onExport: () => exportListXlsx({
        menu: '자산관리', filename: `IMS_${label}_${new Date().toISOString().slice(0, 10)}.xlsx`, sheetName: label,
        columns: [
          { label: '제목', value: n => n.title || '', wch: 20 }, { label: '내용', value: n => n.content || '', wch: 40 }, { label: '작성자', value: n => n.created_by || '', wch: 10 }, { label: '작성일', value: n => n.created_at?.slice(0, 16) || '', wch: 16 },
        ], rows: notes,
      }),
    });
    return () => registerPrintExport(null);
  }, [notes, label]); // eslint-disable-line

  const submit = async () => {
    if (!content.trim()) return alert('내용을 입력해주세요.');
    await API.post('/assets/tab-notes', { tab_id: tabId, title: title.trim(), content: content.trim() });
    setTitle(''); setContent(''); load();
  };
  const startEdit = (n) => { setEditingId(n.id); setEditForm({ title: n.title || '', content: n.content }); };
  const saveEdit = async () => {
    if (!editForm.content.trim()) return alert('내용을 입력해주세요.');
    await API.put(`/assets/tab-notes/${editingId}`, editForm);
    setEditingId(null); load();
  };
  const remove = async (id) => { if (!window.confirm('삭제하시겠습니까?')) return; await API.delete(`/assets/tab-notes/${id}`); load(); };

  return (
    <Section title={label}>
      {canWrite && (
        <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: 12, marginBottom: 14, background: '#f8fafc' }}>
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="제목(선택)" style={{ ...input, width: '100%', boxSizing: 'border-box', marginBottom: 8 }} />
          <textarea value={content} onChange={e => setContent(e.target.value)} rows={3} placeholder="내용을 입력해주세요." style={{ ...input, width: '100%', boxSizing: 'border-box', marginBottom: 8, resize: 'vertical' }} />
          <button onClick={submit} style={smallBtn('#185FA5')}>등록</button>
        </div>
      )}
      {notes.length === 0 ? (
        <div style={{ padding: '30px 0', textAlign: 'center', color: '#aaa', fontSize: 13 }}>등록된 내용이 없습니다.</div>
      ) : notes.map(n => (
        <div key={n.id} style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: 12, marginBottom: 8 }}>
          {editingId === n.id ? (
            <div>
              <input value={editForm.title} onChange={e => setEditForm({ ...editForm, title: e.target.value })} placeholder="제목(선택)" style={{ ...input, width: '100%', boxSizing: 'border-box', marginBottom: 8 }} />
              <textarea value={editForm.content} onChange={e => setEditForm({ ...editForm, content: e.target.value })} rows={3} style={{ ...input, width: '100%', boxSizing: 'border-box', marginBottom: 8, resize: 'vertical' }} />
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={saveEdit} style={smallBtn('#185FA5')}>저장</button>
                <button onClick={() => setEditingId(null)} style={smallBtn('#e5e7eb', '#555')}>취소</button>
              </div>
            </div>
          ) : (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                {n.title && <div style={{ fontWeight: 600, fontSize: 14 }}>{n.title}</div>}
                {canWrite && (
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <button onClick={() => startEdit(n)} style={{ background: 'none', border: 'none', color: '#185FA5', fontSize: 12, cursor: 'pointer' }}>수정</button>
                    <button onClick={() => remove(n.id)} style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: 12, cursor: 'pointer' }}>삭제</button>
                  </div>
                )}
              </div>
              <div style={{ whiteSpace: 'pre-wrap', fontSize: 13.5, marginTop: 4 }}>{n.content}</div>
              <div style={{ fontSize: 11, color: '#aaa', marginTop: 6 }}>{n.created_by} · {n.created_at?.slice(0, 16)}</div>
            </div>
          )}
        </div>
      ))}
    </Section>
  );
}

// 2026-09(12차) 요청 — "고장수리" 관리항목(tab_type='repair') 전용 화면. 화장실수리/수도설비수리/
// 전기수리/자동문/유리문 등 기타 시설의 고장수리 접수·처리 내역을 관리한다. 자산목록형
// (AssetListTab)과 달리 "자산 1건 등록"이 아니라 "수리이력 1건 등록"이라서 자산에 등록되어 있지
// 않은 시설물도 위치/내용만으로 자유롭게 남길 수 있고(asset_id 연결은 선택), 목록도 자산목록과는
// 다르게 유형/상태/검색 필터가 있는 이력 테이블 형태로 구성한다.
// 2026-09(15차) 요청 — 이 탭의 목적을 "시설/설비 고장시 대처방안 + 수리업체를 미리 등록해두어
// 근로자가 확인 후 1차 대응할 수 있게 하는 것"으로 재정의했다. 등록 버튼은 다른 탭과 동일하게
// 페이지 상단 공통 버튼 줄로 옮기고(AssetsPage에서 처리), 필터도 탭 줄 우측(다른 유형탭의
// 검색창 자리)으로 옮겨 화면 구성 통일감을 준다.
const REPAIR_TYPES = ['화장실수리', '수도설비수리', '전기수리', '자동문', '유리문', '기타'];
const REPAIR_STATUS = ['접수', '진행중', '완료'];
const REPAIR_STATUS_COLOR = { '접수': '#B45309', '진행중': '#185FA5', '완료': '#16a34a' };

// 2026-09(15차) 요청 — 고장수리 등록폼 재구성: "시설/설비별로 고장시 대처방안과 수리업체를
// 등록해 근로자가 확인 후 처리할 수 있도록" 하는 목적에 맞춰, 가장 먼저 눈에 띄는 자리에
// "고장시 대처방안"을 배치하고 수리업체 정보를 한 묶음으로 구성했다. 또한 교체/부품비 등
// 비용이 발생하는 수리는 관리부 승인(품의)이 필요하므로 체크박스로 표시할 수 있게 했다 —
// 다만 전자결재의 품의서 양식이 아직 등록되어 있지 않아, 체크 시 자동으로 품의서를 만드는
// 연동은 아직 없고 "승인 필요" 표시 + 메모만 남긴다(추후 품의서 양식 등록 후 자동연동 예정).
function RepairLogForm({ tabId, editData, onClose, onSaved }) {
  const [form, setForm] = useState({
    facility_name: editData?.facility_name || '',
    repair_type: editData?.repair_type || '화장실수리',
    repair_type_etc: editData?.repair_type_etc || '',
    location: editData?.location || '',
    department: editData?.department || '',
    status: editData?.status || '접수',
    response_plan: editData?.response_plan || '',
    vendor_name: editData?.vendor_name || '',
    handler_name: editData?.handler_name || '',
    manager_contact: editData?.manager_contact || '',
    content: editData?.content || '',
    request_date: editData?.request_date || '',
    complete_date: editData?.complete_date || '',
    cost: editData?.cost ?? '',
    needs_approval: editData?.needs_approval ? true : false,
    approval_note: editData?.approval_note || '',
  });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const ch = e => setForm({ ...form, [e.target.name]: e.target.value });

  useEffect(() => {
    API.get('/org/public').then(r => setDepartments(r.data?.departments || [])).catch(() => {});
  }, []);

  const submit = async () => {
    if (!form.facility_name.trim()) return alert('시설/설비명을 입력해주세요.');
    setLoading(true);
    const fd = new FormData();
    fd.append('tab_id', tabId);
    Object.entries(form).forEach(([k, v]) => {
      if (k === 'needs_approval') { fd.append(k, v ? '1' : '0'); return; }
      fd.append(k, v ?? '');
    });
    if (file) fd.append('file', file);
    try {
      if (editData) await API.put(`/assets/repair-logs/${editData.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      else await API.post('/assets/repair-logs', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onSaved();
    } catch (e) { alert(e.response?.data?.message || '오류가 발생했습니다.'); } finally { setLoading(false); }
  };

  return (
    <div style={overlayStyle}>
      <div style={{ ...modalStyle, width: 640 }} onClick={e => e.stopPropagation()}>
        <ModalHeader title={editData ? '🔧 고장수리 항목 수정' : '🔧 고장수리 항목 등록'} onClose={onClose} />
        <div style={{ fontSize: 12.5, color: '#888', margin: '-4px 0 12px' }}>
          시설/설비에 고장이 발생했을 때 근로자가 참고할 대처방안과 연락할 수리업체를 미리 등록해두는 화면입니다.
        </div>

        <div><label style={labelStyle}>시설/설비명 *</label><input name="facility_name" value={form.facility_name} onChange={ch} style={formInput} placeholder="예: 3층 남자화장실 세면대" /></div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div>
            <label style={labelStyle}>구분</label>
            <select name="repair_type" value={form.repair_type} onChange={ch} style={formInput}>
              {REPAIR_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          {form.repair_type === '기타' && (
            <div>
              <label style={labelStyle}>기타 구분명</label>
              <input name="repair_type_etc" value={form.repair_type_etc} onChange={ch} style={formInput} placeholder="예: 방충망 수리" />
            </div>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <div><label style={labelStyle}>시설위치</label><input name="location" value={form.location} onChange={ch} style={formInput} placeholder="예: 3층 남자화장실" /></div>
          <div><label style={labelStyle}>관리부서</label>
            <select name="department" value={form.department} onChange={ch} style={formInput}>
              <option value="">선택안함</option>
              {departments.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div><label style={labelStyle}>상태</label>
            <select name="status" value={form.status} onChange={ch} style={formInput}>
              {REPAIR_STATUS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        {/* 이 화면의 핵심 항목 — 근로자가 고장 발생시 가장 먼저 확인할 내용이므로 눈에 띄게 배치 */}
        <div style={{ background: '#FFF7ED', border: '1px solid #FDE9CF', borderRadius: 8, padding: '10px 12px', margin: '4px 0 10px' }}>
          <label style={{ ...labelStyle, color: '#B45309' }}>⚠️ 고장시 대처방안 *근로자 확인용*</label>
          <textarea name="response_plan" value={form.response_plan} onChange={ch} rows={3}
            style={{ ...formInput, resize: 'vertical', background: '#fff' }}
            placeholder="예: 즉시 밸브를 잠그고 사용을 중지한 뒤, 아래 수리업체로 연락하세요. 1차 조치가 어려우면 관리부서로 연락." />
        </div>

        {/* 수리업체명 + 담당자 + 연락처 — 근로자가 고장시 바로 연락할 수 있도록 등록 */}
        <div>
          <label style={labelStyle}>수리업체 연락처</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <input name="vendor_name" value={form.vendor_name} onChange={ch} style={formInput} placeholder="업체명" />
            <input name="handler_name" value={form.handler_name} onChange={ch} style={formInput} placeholder="담당자 (예: 과장 홍길동)" />
            <input name="manager_contact" value={form.manager_contact} onChange={ch} style={formInput} placeholder="연락처 (예: 010-1234-5678)" />
          </div>
        </div>

        <div><label style={labelStyle}>수리 내용/이력</label><textarea name="content" value={form.content} onChange={ch} rows={2} style={{ ...formInput, resize: 'vertical' }} placeholder="실제 발생한 고장 및 처리 내용을 기록합니다." /></div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <div><label style={labelStyle}>접수일</label><input type="date" name="request_date" value={form.request_date} onChange={ch} style={formInput} /></div>
          <div><label style={labelStyle}>처리완료일</label><input type="date" name="complete_date" value={form.complete_date} onChange={ch} style={formInput} /></div>
          <div><label style={labelStyle}>소요/예상 비용</label><input name="cost" value={form.cost} onChange={e => setForm({ ...form, cost: formatComma(e.target.value) })} inputMode="numeric" style={formInput} placeholder="0" /></div>
        </div>

        {/* 2026-09(15차) 요청 — 교체/비용 발생시 관리부 승인(품의) 필요 여부. 전자결재에
            품의서 양식이 아직 등록되어 있지 않아 자동 품의서 생성은 지원하지 않으며, 그 전까지는
            체크와 메모로만 "승인 필요" 상태를 남긴다. */}
        <div style={{ border: '1px dashed #cbd5e1', borderRadius: 8, padding: '10px 12px', margin: '4px 0 2px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.needs_approval} onChange={e => setForm({ ...form, needs_approval: e.target.checked })} />
            교체 또는 비용이 발생하는 수리 — 관리부 승인(품의) 필요
          </label>
          {form.needs_approval && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>
                ℹ️ 품의서 양식이 아직 등록되어 있지 않아 자동 품의서 작성은 준비 중입니다. 양식 등록 후 이 항목을 기준으로 자동 연동될 예정이며, 그 전까지는 아래에 필요한 내용을 메모해주세요.
              </div>
              <textarea name="approval_note" value={form.approval_note} onChange={ch} rows={2} style={{ ...formInput, resize: 'vertical' }} placeholder="예: 세면대 전체 교체 필요, 예상 비용 350,000원" />
            </div>
          )}
        </div>

        <div>
          <label style={labelStyle}>첨부파일{editData?.file_name ? ' (교체 시에만 선택)' : ''}</label>
          {editData?.file_name && <div style={{ fontSize: 12.5, marginBottom: 6 }}><a href={`/uploads/${editData.file_name}`} target="_blank" rel="noreferrer">📎 {editData.file_ori || editData.file_name}</a></div>}
          <input type="file" onChange={e => setFile(e.target.files?.[0] || null)} style={{ fontSize: 13, marginBottom: 4 }} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
          <button onClick={onClose} style={smallBtn('#e5e7eb', '#555')}>취소</button>
          <button onClick={submit} disabled={loading} style={smallBtn('#185FA5')}>{loading ? '저장 중...' : '저장'}</button>
        </div>
      </div>
    </div>
  );
}

// 2026-09(15차) 요청 — 등록 버튼/필터는 AssetsPage(부모) 상단 공통 영역으로 옮겼다. 이 탭은
// 다른 유형탭(AssetListTab)처럼 제목·액션 버튼이 없는 <Section>만으로 목록을 구성해 화면
// 구성에 통일감을 준다.
function RepairLogTab({ tabId, label, canWrite, canDelete, registerPrintExport, repairFormTrigger, consumeRepairFormTrigger, typeFilter, statusFilter, search }) {
  const [logs, setLogs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => API.get('/assets/repair-logs', {
    params: { tab_id: tabId, repair_type: typeFilter || undefined, status: statusFilter || undefined, search: search || undefined },
  }).then(r => setLogs(r.data)).catch(() => {});
  useEffect(() => { load(); }, [tabId, typeFilter, statusFilter, search]); // eslint-disable-line

  // 우측상단 고정 "고장수리등록" 버튼(다른 탭에 있을 때 눌러도 이 탭으로 전환된 뒤 등록폼이 열린다)
  useEffect(() => {
    if (consumeRepairFormTrigger && consumeRepairFormTrigger(repairFormTrigger)) {
      setEditing(null);
      setShowForm(true);
    }
  }, [repairFormTrigger]); // eslint-disable-line

  useEffect(() => {
    registerPrintExport({
      onPrint: () => printListWindow({
        menu: '자산관리', title: `🔧 ${label}`,
        columns: [
          { label: '구분', value: r => r.repair_type === '기타' ? (r.repair_type_etc || '기타') : r.repair_type },
          { label: '시설/설비명', value: r => r.facility_name }, { label: '시설위치', value: r => r.location },
          { label: '관리부서', value: r => r.department }, { label: '고장시 대처방안', value: r => r.response_plan },
          { label: '수리업체', value: r => r.vendor_name }, { label: '담당자', value: r => r.handler_name },
          { label: '연락처', value: r => r.manager_contact }, { label: '수리내용/이력', value: r => r.content },
          { label: '접수일', value: r => r.request_date }, { label: '처리완료일', value: r => r.complete_date },
          { label: '소요/예상비용', value: r => r.cost }, { label: '승인필요', value: r => r.needs_approval ? 'Y' : '' },
          { label: '상태', value: r => r.status },
        ], rows: logs, listName: `${label}(${logs.length}건)`,
      }),
      onExport: () => exportListXlsx({
        menu: '자산관리', filename: `IMS_${label}_${new Date().toISOString().slice(0, 10)}.xlsx`, sheetName: label,
        columns: [
          { label: '구분', value: r => (r.repair_type === '기타' ? (r.repair_type_etc || '기타') : r.repair_type) || '', wch: 12 },
          { label: '시설/설비명', value: r => r.facility_name || '', wch: 18 }, { label: '시설위치', value: r => r.location || '', wch: 16 },
          { label: '관리부서', value: r => r.department || '', wch: 12 }, { label: '고장시 대처방안', value: r => r.response_plan || '', wch: 32 },
          { label: '수리업체', value: r => r.vendor_name || '', wch: 12 }, { label: '담당자', value: r => r.handler_name || '', wch: 10 },
          { label: '연락처', value: r => r.manager_contact || '', wch: 14 }, { label: '수리내용/이력', value: r => r.content || '', wch: 28 },
          { label: '접수일', value: r => r.request_date || '', wch: 12 }, { label: '처리완료일', value: r => r.complete_date || '', wch: 12 },
          { label: '소요/예상비용', value: r => r.cost || 0, wch: 12 }, { label: '승인필요', value: r => r.needs_approval ? 'Y' : '', wch: 8 },
          { label: '상태', value: r => r.status || '', wch: 8 },
        ], rows: logs,
      }),
    });
    return () => registerPrintExport(null);
  }, [logs, label]); // eslint-disable-line

  const remove = async (id) => {
    if (!window.confirm('삭제하시겠습니까?')) return;
    try { await API.delete(`/assets/repair-logs/${id}`); load(); } catch (e) { alert(e.response?.data?.message || '오류가 발생했습니다.'); }
  };

  return (
    <Section>
      {logs.length === 0 ? (
        <div style={{ padding: '30px 0', textAlign: 'center', color: '#aaa', fontSize: 13 }}>등록된 고장수리 항목이 없습니다. 우측 상단 "고장수리등록" 버튼으로 시설별 대처방안과 수리업체를 등록해주세요.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${C.border}`, textAlign: 'left', color: '#666' }}>
                <th style={{ padding: '8px 6px' }}>구분</th>
                <th style={{ padding: '8px 6px' }}>시설/설비명</th>
                <th style={{ padding: '8px 6px' }}>위치/관리부서</th>
                <th style={{ padding: '8px 6px' }}>고장시 대처방안</th>
                <th style={{ padding: '8px 6px' }}>수리업체/담당자</th>
                <th style={{ padding: '8px 6px' }}>접수일</th>
                <th style={{ padding: '8px 6px' }}>처리완료일</th>
                <th style={{ padding: '8px 6px' }}>상태</th>
                <th style={{ padding: '8px 6px' }}>승인</th>
                <th style={{ padding: '8px 6px' }}>첨부</th>
                <th style={{ padding: '8px 6px' }}></th>
              </tr>
            </thead>
            <tbody>
              {logs.map(r => (
                <tr key={r.id} style={{ borderBottom: `1px solid #f0f0f0` }}>
                  <td style={{ padding: '8px 6px' }}>{r.repair_type === '기타' ? (r.repair_type_etc || '기타') : r.repair_type}</td>
                  <td style={{ padding: '8px 6px' }}>{r.facility_name}{r.linked_asset_name && <div style={{ fontSize: 11, color: '#185FA5' }}>🔗 {r.linked_asset_name}</div>}</td>
                  <td style={{ padding: '8px 6px' }}>{r.location}{r.department ? ` / ${r.department}` : ''}</td>
                  <td style={{ padding: '8px 6px', maxWidth: 260, whiteSpace: 'pre-wrap' }}>{r.response_plan}</td>
                  <td style={{ padding: '8px 6px' }}>
                    {r.vendor_name}{r.vendor_name && r.handler_name ? ' / ' : ''}{r.handler_name}
                    {r.manager_contact && <div style={{ fontSize: 11, color: '#888' }}>{r.manager_contact}</div>}
                  </td>
                  <td style={{ padding: '8px 6px' }}>{r.request_date}</td>
                  <td style={{ padding: '8px 6px' }}>{r.complete_date}</td>
                  <td style={{ padding: '8px 6px' }}>
                    <span style={{ fontSize: 11.5, fontWeight: 600, color: REPAIR_STATUS_COLOR[r.status] || '#555', background: `${REPAIR_STATUS_COLOR[r.status] || '#888'}18`, borderRadius: 5, padding: '2px 8px' }}>{r.status}</span>
                  </td>
                  <td style={{ padding: '8px 6px' }}>
                    {!!r.needs_approval && <span style={{ fontSize: 11, fontWeight: 600, color: '#B45309', background: '#FEF3C7', borderRadius: 5, padding: '2px 8px' }}>승인필요</span>}
                  </td>
                  <td style={{ padding: '8px 6px' }}>{r.file_name && <a href={`/uploads/${r.file_name}`} target="_blank" rel="noreferrer">📎</a>}</td>
                  <td style={{ padding: '8px 6px', whiteSpace: 'nowrap' }}>
                    {canWrite && <button onClick={() => { setEditing(r); setShowForm(true); }} style={{ background: 'none', border: 'none', color: '#185FA5', fontSize: 12, cursor: 'pointer', marginRight: 8 }}>수정</button>}
                    {canDelete && <button onClick={() => remove(r.id)} style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: 12, cursor: 'pointer' }}>삭제</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <RepairLogForm tabId={tabId} editData={editing} onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }} />
      )}
    </Section>
  );
}

// ══════════════════════════════════════════════════════════════
// 9) 감가설정 모달 — 이미 등록된 자산들에 감가상각계정/상각제외를 개별·일괄 지정
//    (감가상각 탭의 계정과목관리와는 별개 — 계정 자체를 만드는 곳이 아니라 이미 만들어진
//    계정을 자산에 빠르게 매칭시키는 도구)
// ══════════════════════════════════════════════════════════════
// 2026-09-13 추가 — 감가설정 모달 "자산기준" 컬럼용 고정 매핑표. 관리항목(asset_menu_tabs)의
// label(=division_label)을 키로, 세법상 자산 분류 표기를 값으로 가진다(요청사항 그대로 하드코딩).
const ASSET_DEP_STANDARD_MAP = {
  '비품자산': '기구 및 비품',
  '소프트웨어': '무형자산 (개발비/소프트웨어)',
  '장비자산': '공구, 기구 및 비품(전산기기)',
  '공구자산': '공구',
  '설비&시설': '기계장치 및 플랜트설비',
  '기타자산': '차량, 운반구, 지게차, 렌탈등',
};
// 자산코드(예: NI-EQ-0001)에서 채번 일련번호(-0001)를 떼어내 접두어(NI-EQ)만 남긴다.
function assetCodePrefix(code) {
  if (!code) return '-';
  return code.replace(/-\d+$/, '');
}

// 2026-09-13 전면개편 — 사용자 피드백: "감가설정은 개별 자산이 아니라 관리항목(탭)별로
// 감가상각 기준을 정하는 곳"이라는 요구에 맞춰, 개별 자산 나열 방식(위 ASSET_DEP_STANDARD_MAP/
// assetCodePrefix를 이용한 자산별 리스트)을 걷어내고 asset_menu_tabs(관리항목) 자체를
// 한 행씩 보여주는 방식으로 다시 작성한다. 여기서 설정한 감가상각계정/감가제외 여부는
// PUT /assets/menu-tabs/:id 로 저장되고, 그 탭으로 신규 자산을 등록할 때 자동으로
// 반영된다(AssetForm의 depAccount/기본 depreciation_exempt 로직 참고).
function DepreciationQuickSetModal({ onClose }) {
  const [tabs, setTabs] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [msg, setMsg] = useState('');

  const load = () => {
    API.get('/assets/menu-tabs').then(r => setTabs((r.data || []).filter(t => t.tab_key !== 'overview' && t.tab_key !== 'depreciation' && t.tab_type !== 'repair'))).catch(() => {});
    API.get('/assets/depreciation/accounts').then(r => setAccounts(r.data)).catch(() => {});
  };
  useEffect(load, []);
  useEffect(() => { if (!msg) return; const t = setTimeout(() => setMsg(''), 2500); return () => clearTimeout(t); }, [msg]);

  const applyTab = async (t, fields) => {
    try { await API.put(`/assets/menu-tabs/${t.id}`, fields); setMsg('저장되었습니다.'); load(); }
    catch (e) { alert(e.response?.data?.message || '오류가 발생했습니다.'); }
  };

  // 2026-09-13(2차) 추가 — "감가상각" 컬럼을 계정명 하나짜리 select 대신, 상각방법(정액법/
  // 정률법)과 내용연수(년수) 두 개의 select로 나눠 선택하게 해달라는 요청. 그 조합에 맞는
  // depreciation_accounts 행을 찾거나 없으면 새로 만든 뒤(POST .../resolve), 그 계정을 탭의
  // 기본계정(default_dep_account_id)으로 지정한다 — 기존 아키텍처(계정 1개를 탭에 연결)는
  // 그대로 유지하면서 입력 UX만 단순화하는 방식이다.
  const applyMethodYears = async (t, method, usefulLife) => {
    try {
      const r = await API.post('/assets/depreciation/accounts/resolve', { method, useful_life: usefulLife });
      await applyTab(t, { default_dep_account_id: r.data.id });
    } catch (e) { alert(e.response?.data?.message || '오류가 발생했습니다.'); }
  };
  const USEFUL_LIFE_OPTIONS = [3, 4, 5, 6, 10];

  return (
    <div style={overlayStyle}>
      <div style={{ ...modalStyle, width: 780 }} onClick={e => e.stopPropagation()}>
        <ModalHeader title="📉 감가설정 — 관리항목별 감가상각 기준 지정" onClose={onClose} />
        <div style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>
          자산 개별 지정이 아니라, 비품자산/소프트웨어 등 관리항목(유형)별로 감가상각 기준을 정합니다.
          여기서 설정한 상각방법/내용연수·감가제외 여부는 해당 유형으로 신규 자산을 등록할 때 자동으로
          반영됩니다(등록시 개별 자산에서 제외할 수 있습니다). 자산기준은 "관리항목" 버튼에서 등록한
          설명이 그대로 표기됩니다.
        </div>
        {msg && <div style={{ background: '#d1fae5', color: '#065f46', padding: '8px 14px', borderRadius: 8, marginBottom: 12, fontSize: 13 }}>{msg}</div>}

        <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '100px 80px 1fr 100px 90px 70px', ...th, padding: '9px' }}>
            <span>구분</span><span>자산코드</span><span>자산기준</span><span>상각방법</span><span>내용연수</span><span>감가제외</span>
          </div>
          {tabs.length === 0 ? <div style={{ padding: '30px 0', textAlign: 'center', color: '#aaa', fontSize: 13 }}>등록된 관리항목이 없습니다.</div> : tabs.map((t, i) => {
            const acc = t.default_dep_account_id ? accounts.find(ac => ac.id === t.default_dep_account_id) : null;
            // 2026-09-13(3차) 버그수정 — 계정이 아직 지정 안 된(acc===null) 탭도 select가
            // "정액법/5년"을 이미 선택된 것처럼 보여줘서, 사용자가 "이미 설정돼 있다"고 착각하고
            // 값을 건드리지 않아 default_dep_account_id가 끝내 저장되지 않는 문제가 있었다(잔존가액
            // 옆 감가상각 표기가 계속 안 뜨는 원인). 미지정 상태는 빈 값("미지정")으로 명시하고,
            // 행 배경을 옅은 빨강으로 강조해 눈에 띄게 한다. 값을 실제로 "선택"해야만(같은 값이라도)
            // onChange가 발생해 계정이 생성/저장된다.
            const curMethod = acc?.method || '';
            const curYears = acc?.useful_life || '';
            const unset = !acc;
            return (
              <div key={t.id} style={{ display: 'grid', gridTemplateColumns: '100px 80px 1fr 100px 90px 70px', ...td, borderTop: i > 0 ? undefined : 'none', background: unset ? '#FEF2F2' : undefined }}>
                <span style={{ fontWeight: 600 }}>{t.label}</span>
                <span>{t.code_prefix || '-'}</span>
                <span style={{ textAlign: 'left', color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={t.description || ''}>
                  {t.description || '-'}
                </span>
                <span>
                  <select value={curMethod} onChange={e => applyMethodYears(t, e.target.value, curYears || 5)} style={{ ...input, padding: '4px 6px', fontSize: 12, color: unset ? '#dc2626' : undefined, fontWeight: unset ? 700 : undefined }}>
                    <option value="">미지정</option>
                    <option value="정액법">정액법</option>
                    <option value="정률법">정률법</option>
                  </select>
                </span>
                <span>
                  <select value={curYears} onChange={e => applyMethodYears(t, curMethod || '정액법', Number(e.target.value))} style={{ ...input, padding: '4px 6px', fontSize: 12, color: unset ? '#dc2626' : undefined, fontWeight: unset ? 700 : undefined }}>
                    <option value="">-</option>
                    {USEFUL_LIFE_OPTIONS.map(y => <option key={y} value={y}>{y}년</option>)}
                  </select>
                </span>
                <span>
                  <input type="checkbox" checked={!!t.dep_exempt} onChange={e => applyTab(t, { dep_exempt: e.target.checked })} />
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// 재물조사/폐기항목 버튼 삭제로 접근 경로가 없어져 dead code가 되어 함께 제거했다.

// ══════════════════════════════════════════════════════════════
// 2026-08-30 — 자산종합 탭: 6개 자산유형별 건수/취득가액 합계 카드(클릭시 해당 탭으로 이동),
// 8탭 개편 이전에 등록되어 유형이 비어있는("미분류") 자산을 재분류하는 목록, 최근 등록 5건을
// 보여준다. 별도 인쇄/내보내기는 두지 않는다(카드형 대시보드).
// ══════════════════════════════════════════════════════════════
// 2026-08-31 — 상단 "자산 현황 요약" 카드는 AssetsPage 헤더로 이동했다(모든 탭에서 항상
// 보이도록). 이 컴포넌트는 이제 summary/loadSummary를 상위(AssetsPage)의 ctx로부터 받아
// 미분류 자산 재분류 UI와 최근 등록 자산 목록만 렌더링한다.
// 2026-09(14차) 요청: "자산관리 - 자산종합"을 목록형에서 유형별 카드 그리드로 전면 개편.
// 카드마다 전체수량/장부미등록수량/총자산금액/잔존가액 4항목을 표시하고, 클릭하면 해당 유형탭으로
// 바로 이동한다(요청사항 그대로). 그 아래 미분류 자산 보정 섹션과 최근 등록 자산 섹션은 유지하되
// UI 중심 요청에 맞춰 카드/타임라인 형태로 다듬었다.
function OverviewTab({ summary, loadSummary, setActiveTab, registerPrintExport }) {
  const [unclassified, setUnclassified] = useState([]);
  const [msg, setMsg] = useState('');

  const loadUnclassified = () => API.get('/assets').then(r => setUnclassified(r.data.filter(a => !a.asset_type))).catch(() => {});
  useEffect(() => { loadUnclassified(); }, []);
  useEffect(() => { if (!msg) return; const t = setTimeout(() => setMsg(''), 2500); return () => clearTimeout(t); }, [msg]);

  const assignType = async (id, asset_type) => {
    if (!asset_type) return;
    try {
      await API.patch(`/assets/${id}/asset-type`, { asset_type });
      setMsg('자산유형이 지정되었습니다.');
      loadUnclassified(); loadSummary();
    } catch (e) { alert(e.response?.data?.message || '오류가 발생했습니다.'); }
  };

  // 2026-10 요청: 자산종합에서도 우측상단 인쇄/내보내기 사용 — 유형별 카드 요약표를 대상으로 한다.
  useEffect(() => {
    if (!registerPrintExport || !summary) return undefined;
    const types = (summary.types || []).filter(t => t.tab_type !== 'repair');
    const depText = (t) => {
      if (!t.dep_method) return '미설정';
      const rate = t.dep_method === '정률법'
        ? (t.dep_rate != null ? Number(t.dep_rate) : null)
        : (t.dep_useful_life ? 100 / Number(t.dep_useful_life) : null);
      return `${t.dep_method}${rate != null ? ` ${rate.toFixed(1)}%/년` : ''}`;
    };
    const ratio = (t) => (t.total_price ? `${Math.round((t.total_residual / t.total_price) * 1000) / 10}%` : '-');
    const label = (t) => stripIcon(t.label);
    const rows = [...types, {
      __total: true, label: '합계',
      count: types.reduce((a, t) => a + (t.count || 0), 0),
      unregistered_count: types.reduce((a, t) => a + (t.unregistered_count || 0), 0),
      total_price: types.reduce((a, t) => a + (t.total_price || 0), 0),
      total_residual: types.reduce((a, t) => a + (t.total_residual || 0), 0),
    }];
    const cols = (forExcel) => [
      { label: '자산유형', value: r => r.__total ? '합계' : label(r), wch: 16 },
      { label: '전체수량', value: r => forExcel ? (r.count || 0) : `${r.count || 0}건`, wch: 10 },
      { label: '장부미등록', value: r => forExcel ? (r.unregistered_count || 0) : `${r.unregistered_count || 0}건`, wch: 12 },
      { label: '감가상각', value: r => r.__total ? '' : depText(r), wch: 18 },
      { label: '총자산금액', value: r => forExcel ? Math.round(r.total_price || 0) : moneyFmt(Math.round(r.total_price || 0)), wch: 16 },
      { label: '잔존가액', value: r => forExcel ? Math.round(r.total_residual || 0) : moneyFmt(Math.round(r.total_residual || 0)), wch: 16 },
      { label: '잔존률', value: r => ratio(r), wch: 10 },
    ];
    registerPrintExport({
      onPrint: () => printListWindow({ menu: '자산관리', title: '🗃️ 자산종합', columns: cols(false), rows, listName: '자산종합' }),
      onExport: () => exportListXlsx({ menu: '자산관리', filename: `IMS_자산종합_${new Date().toISOString().slice(0, 10)}.xlsx`, sheetName: '자산종합', columns: cols(true), rows }),
    });
    return () => registerPrintExport(null);
  }, [summary]); // eslint-disable-line

  if (!summary) return <div style={{ padding: 60, textAlign: 'center', color: '#aaa', fontSize: 14 }}>불러오는 중...</div>;

  // 고장수리형 탭(tab_type='repair')은 assets 테이블 집계 대상이 아니라 별도 이력 화면이므로
  // 자산종합 카드에서는 제외한다(상단 카드메뉴와 동일한 규칙).
  const cardTypes = (summary.types || []).filter(t => t.tab_type !== 'repair');
  const fmtMoney = (n) => `${Math.round(n || 0).toLocaleString()}원`;
  // 2026-09(16차) 요청 — 카드 "전체수량" 아래에 그 유형에 적용된 감가상각방법/연간상각률을
  // 함께 표기한다. 정액법은 상각률을 따로 저장하지 않으므로(내용연수의 역수) 여기서 계산하고,
  // 정률법은 depreciation_accounts.dep_rate(%)를 그대로 쓴다. 기본계정이 연결 안 된 유형은
  // "미설정"으로 표시해 감가설정에서 계정을 지정하도록 안내한다.
  const fmtDepRate = (t) => {
    if (!t.dep_method) return '미설정';
    const rate = t.dep_method === '정률법'
      ? (t.dep_rate != null ? Number(t.dep_rate) : null)
      : (t.dep_useful_life ? 100 / Number(t.dep_useful_life) : null);
    return `${t.dep_method}${rate != null ? ` · ${rate.toFixed(1)}%/년` : ''}`;
  };
  // 2026-09(17차) 요청 — 카드에 "잔존률"(잔존가액 ÷ 총자산금액)을 별도로 표기한다.
  const fmtRatio = (t) => {
    if (!t.total_price) return '-';
    return `${Math.round((t.total_residual / t.total_price) * 1000) / 10}%`;
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      {/* 2026-09(19차) 요청 — "전체 자산 수량/전체 자산금액/전체 잔존가액" 요약 리본은
          AssetsPage 헤더(모든 탭 공통 상단)로 이동했으므로 여기서는 더 이상 그리지 않는다. */}

      {/* 유형별 카드 그리드 — 클릭시 해당 탭으로 이동. 2026-09(15차) 요청: 한 줄에 3개씩,
          카드 간격을 넓혀 표시한다. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 28 }}>
        {cardTypes.map(t => {
          const c = ASSET_SUMMARY_COLORS[t.asset_type] || ASSET_SUMMARY_COLOR_FALLBACK;
          return (
            <div key={t.asset_type} onClick={() => setActiveTab && setActiveTab(t.asset_type)}
              style={{
                background: '#fff', border: `1px solid ${C.border}`, borderTop: `4px solid ${c.color}`,
                borderRadius: 14, padding: '16px 18px 18px', cursor: 'pointer',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <span style={{ fontSize: 14.5, fontWeight: 700, color: c.color }}>{stripIcon(t.label)}</span>
                <span style={{ fontSize: 10.5, fontWeight: 700, color: c.color, background: c.bg, borderRadius: 20, padding: '3px 10px' }}>{t.count}건</span>
              </div>
              {/* 2026-09(17차) 요청 — 3열×2행(전체수량/장부미등록/감가상각, 총자산금액/잔존가액/
                  잔존률) 레이아웃으로 개편 + 라벨 글자크기 13px로 확대. */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', rowGap: 12, columnGap: 8 }}>
                <div>
                  <div style={{ fontSize: 13, color: '#999', marginBottom: 3 }}>전체수량</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#333' }}>{t.count}건</div>
                </div>
                <div>
                  <div style={{ fontSize: 13, color: '#999', marginBottom: 3 }}>장부미등록</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: t.unregistered_count > 0 ? '#b91c1c' : '#333' }}>{t.unregistered_count}건</div>
                </div>
                <div>
                  <div style={{ fontSize: 13, color: '#999', marginBottom: 3 }}>감가상각</div>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: '#333' }}>{fmtDepRate(t)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 13, color: '#999', marginBottom: 3 }}>총자산금액</div>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: '#333' }}>{fmtMoney(t.total_price)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 13, color: '#999', marginBottom: 3 }}>잔존가액</div>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: '#333' }}>{fmtMoney(t.total_residual)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 13, color: '#999', marginBottom: 3 }}>잔존률</div>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: '#333' }}>{fmtRatio(t)}</div>
                </div>
              </div>
            </div>
          );
        })}
        {cardTypes.length === 0 && (
          <div style={{ gridColumn: '1/-1', padding: '40px 0', textAlign: 'center', color: '#aaa', fontSize: 13 }}>
            관리항목에 등록된 자산유형 탭이 없습니다. 우측 상단 "📑 관리항목"에서 탭을 추가해주세요.
          </div>
        )}
      </div>

      {summary.unclassified.count > 0 && (
        <Section title={`⚠️ 미분류 자산 (${summary.unclassified.count}건) — 유형을 지정해주세요`}>
          {msg && <div style={{ background: '#d1fae5', color: '#065f46', padding: '8px 14px', borderRadius: 8, marginBottom: 12, fontSize: 13 }}>{msg}</div>}
          <div style={{ fontSize: 12.5, color: '#888', marginBottom: 10 }}>
            8개 탭 개편 이전에 등록된 자산은 유형이 지정되어 있지 않아 자산유형 탭에 나타나지 않습니다. 아래에서 유형을 선택하면 해당 탭으로 이동합니다.
          </div>
          <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
            {unclassified.map((a, i) => (
              <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', borderTop: i > 0 ? '1px solid #f0f0f0' : 'none' }}>
                <span style={{ fontSize: 12, color: '#aaa', width: 100 }}>{a.asset_code || '-'}</span>
                <span style={{ flex: 1, fontSize: 13.5, fontWeight: 600 }}>{a.asset_name}</span>
                <span style={{ fontSize: 12.5, color: '#888' }}>{a.category_name || '-'}</span>
                <select defaultValue="" onChange={e => assignType(a.id, e.target.value)} style={{ ...input, padding: '5px 10px', fontSize: 12.5, width: 150 }}>
                  <option value="" disabled>유형 선택</option>
                  {Object.entries(ASSET_TYPE_META).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
                </select>
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

// 2026-08-30 — 집기자산/전산자산/소프트웨어/장비자산/공구자산/비품자산 6개 탭: 통합
// 자산목록 화면(AssetListTab)을 asset_type으로 필터링/자동지정하는 방식으로 재사용해,
// 탭마다 완전히 독립된 등록/목록 화면으로 보이게 한다(사용자가 명시적으로 선택한 방식).
function FurnitureTab(props) { return <AssetListTab {...props} assetType="furniture" typeLabel="집기자산" />; }
function ITTab(props) { return <AssetListTab {...props} assetType="it" typeLabel="전산자산" />; }
function SoftwareTab(props) { return <AssetListTab {...props} assetType="software" typeLabel="소프트웨어" />; }
function EquipmentTab(props) { return <AssetListTab {...props} assetType="equipment" typeLabel="장비자산" />; }
function ToolTab(props) { return <AssetListTab {...props} assetType="tool" typeLabel="공구자산" />; }
function SuppliesTab(props) { return <AssetListTab {...props} assetType="supplies" typeLabel="비품자산" />; }

// tab_key → 실제 화면 컴포넌트 매핑. 함수 선언(hoisting) 덕분에 파일 내 어느 위치에서
// 정의하더라도 위에서 선언한 컴포넌트들을 참조할 수 있다.
const BUILTIN_TAB_COMPONENTS = {
  overview: OverviewTab,
  furniture: FurnitureTab,
  it: ITTab,
  software: SoftwareTab,
  equipment: EquipmentTab,
  tool: ToolTab,
  supplies: SuppliesTab,
  depreciation: DepreciationTab,
};
